import React, { useState, useRef, useEffect } from 'react';
import { scrollToTop } from '../utils/scrollUtils';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  LinearProgress,
  Snackbar,
  Alert,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import VideocamIcon from '@mui/icons-material/Videocam';
import StopIcon from '@mui/icons-material/Stop';
import UploadIcon from '@mui/icons-material/Upload';
import WarningIcon from '@mui/icons-material/Warning';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PreviewIcon from '@mui/icons-material/Preview';
import ArrowRight from '@mui/icons-material/ArrowRight';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import GroupsIcon from '@mui/icons-material/Groups';
import SchoolIcon from '@mui/icons-material/School';
import ConstructionIcon from '@mui/icons-material/Construction';
import PublicIcon from '@mui/icons-material/Public';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import DevicesIcon from '@mui/icons-material/Devices';
import FitbitIcon from '@mui/icons-material/Fitbit';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HearingIcon from '@mui/icons-material/Hearing';
import TimerIcon from '@mui/icons-material/Timer';
import StraightenIcon from '@mui/icons-material/Straighten';
import FaceIcon from '@mui/icons-material/Face';
import ElderlyIcon from '@mui/icons-material/Elderly';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { alpha, useTheme } from '@mui/material/styles';
import Flashcard from '../components/Flashcard';
import SubModule from '../components/SubModule';
import ModuleCard from '../components/ModuleCard';
import FlowDiagram from '../components/FlowDiagram';
import CourseModule from '../components/CourseModule';
import Infographic from '../components/Infographic';
import FallRiskFactorsGuide from '../components/FallRiskFactorsGuide';
import HighRiskFactorsGuide from '../components/HighRiskFactorsGuide';
import { analyzeVideoWithGemini, mockAnalyzeVideo } from '../services/geminiService';
import { getCompletedLessons } from '../services/lessonService';
import { useModuleProgress } from '../context/ModuleProgressContext';
import InfoIcon from '@mui/icons-material/Info';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import EmergencyShareIcon from '@mui/icons-material/EmergencyShare';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

// --- Move Lesson Data Definitions Outside Component and Export --- 

export const module1_lessons = [
  { isHeader: true, title: 'Understanding Fall Risks' }, 
  // --- Restructured Lesson 1.1 --- 
  {
    id: "1.1",
    title: "Common Causes of Falls", 
    description: "Falls are rarely caused by a single factor; instead, they typically result from a complex interaction of multiple risk factors. Understanding these common causes is the first step in effective prevention.", 
    duration: 10, 
    type: "Detailed Lesson", 
    // --- Add Full Structured Content Here --- 
    content: { 
      introduction: "Falls represent the leading cause of injury among older adults. Understanding the most common causes and risk factors is essential for implementing effective prevention strategies and ensuring patient safety.",
      videoId: "causes_of_falls",
      videoCaption: "This video demonstrates common causes of falls and how to identify risk factors in various environments.",
      keyStats: [
        "More than 1 in 4 older adults (65+) fall each year, with 36 million falls annually",
        "Every 11 seconds, an older adult is treated in the emergency room for a fall",
        "Falls result in over 3 million ER visits and 800,000 hospitalizations yearly",
        "The healthcare cost burden exceeds $50 billion annually"
      ],
      riskFactors: {
        intrinsic: [
          "Muscle weakness, particularly in lower extremities",
          "Balance and gait problems",
          "Visual impairments and poor depth perception",
          "Cognitive impairment and confusion",
          "Chronic conditions: arthritis, Parkinson's, dementia",
          "Medication effects, especially polypharmacy (4+ medications)"
        ],
        extrinsic: [
          "Poor lighting and high-glare surfaces",
          "Loose rugs and cluttered walkways",
          "Lack of grab bars and handrails",
          "Uneven surfaces and thresholds",
          "Improper footwear (ill-fitting, slippery soles)",
          "Unfamiliar environments (hospital, new living arrangements)"
        ]
      },
      assessmentFramework: {
          title: "Fall Risk Assessment Framework",
          description: "A comprehensive assessment should evaluate these key domains:",
          steps: [
            "Medical History: Review conditions, medications, and previous falls",
            "Physical Assessment: Evaluate gait, balance, and muscle strength",
            "Environmental Assessment: Identify hazards in the living environment",
            "Standardized Testing: Use validated tools like Morse Fall Scale or TUG test",
            "Regular Reassessment: Monitor for changes in condition or environment"
          ]
      },
      evidenceInsights: {
          title: "Evidence-Based Prevention Insights",
          description: "Research has revealed several important insights about fall prevention:",
          insights: [
            `Falls are rarely caused by a single factor; most involve multiple risk factors`,
            `30-50% of falls among older adults are preventable through targeted interventions`,
            `Multifactorial interventions addressing several risk factors show greatest effectiveness`,
            `Regular strength and balance exercises can reduce fall risk by up to 35%`,
            `The fear of falling often leads to reduced activity, paradoxically increasing fall risk`
          ]
      },
      highRiskPopulations: {
          title: "High-Risk Populations",
          groups: [
              {
                  name: "Cognitive Impairment",
                  points: [
                      "2-3 times higher fall risk with dementia",
                      "Impaired judgment may lead to unsafe behaviors",
                      "May not recall or report falls that occur",
                      "Require more frequent supervision",
                      "Benefit from simple, consistent routines"
                  ]
              },
              {
                  name: "Post-Surgical Patients",
                  points: [
                      "Anesthesia effects can persist 24+ hours",
                      "Pain medication may cause dizziness",
                      "Unfamiliar hospital environment increases risk",
                      "Pain may alter normal movement patterns",
                      "Require close monitoring during ambulation"
                  ]
              }
          ]
      },
      
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of fall risk factors",
        questions: [
          {
            question: "Which is NOT considered an intrinsic risk factor for falls?",
            options: ["Muscle weakness", "Poor lighting", "Previous falls", "Vision impairment"],
            correctAnswer: "Poor lighting",
            explanation: "Poor lighting is an extrinsic (environmental) factor, while the others are intrinsic (related to the individual's condition)."
          },
          {
            question: "Which approach has shown the greatest effectiveness for fall prevention?",
            options: ["Medication management only", "Environmental modifications only", "Multifactorial interventions", "Exercise programs only"],
            correctAnswer: "Multifactorial interventions",
            explanation: "Comprehensive approaches addressing multiple risk factors simultaneously show the greatest impact on reducing falls."
          },
          {
            question: "What percentage of falls are estimated to be preventable?",
            options: ["10-20%", "30-50%", "60-70%", "80-90%"],
            correctAnswer: "30-50%",
            explanation: "Current research suggests that 30-50% of falls can be prevented through appropriate interventions targeting modifiable risk factors."
          }
        ]
      }
    } 
    // --- End of Full Structured Content ---
  },
  // --- End of Restructured Lesson 1.1 ---
  {
    id: "1.2",
    title: "Impact of Falls (Interactive)",
    description: "Explore the physical, psychological, and financial impact of falls.",
    duration: 10,
    type: "Interactive",
    content: {
      introduction: "Falls have significant impacts across multiple dimensions of healthcare and patient wellbeing. Understanding these impacts is crucial for implementing effective prevention strategies and communicating their importance to patients and families.",
      videoId: "impact_of_falls",
      videoCaption: "This video illustrates the serious physical, psychological, and financial impacts that falls can have on patients and healthcare systems.",
    
      sections: [
        {
          title: "Physical Impact",
          points: [
            "Fractures (especially hip fractures)",
            "Head injuries and traumatic brain injury",
            "Soft tissue injuries and bruising",
            "Reduced mobility and independence",
            "Increased risk of subsequent falls"
          ]
        },
        {
          title: "Psychological Impact",
          points: [
            "Fear of falling (affects 40-73% of those who have fallen)",
            "Anxiety and depression",
            "Loss of confidence and self-efficacy",
            "Social isolation and withdrawal",
            "Reduced quality of life"
          ]
        },
        {
          title: "Financial Impact",
          points: [
            "Direct medical costs exceed $50 billion annually",
            "Increased length of hospital stay (average 6.3 days longer)",
            "Higher rates of discharge to long-term care facilities",
            "Increased caregiver burden and costs",
            "Lost productivity for family caregivers"
          ]
        }
      ],
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of the impact of falls",
        questions: [
          {
            question: "What percentage of seniors who fall and break a hip die within one year?",
            options: ["10%", "25%", "40%", "60%"],
            correctAnswer: "25%",
            explanation: "Research shows that approximately 25% of seniors who fall and break a hip die within the first year after their injury."
          },
          {
            question: "Which psychological impact affects 40-73% of those who have fallen?",
            options: ["Depression", "Anxiety", "Fear of falling", "Social isolation"],
            correctAnswer: "Fear of falling",
            explanation: "Fear of falling is extremely common after a fall, affecting 40-73% of those who have experienced a fall, and can lead to activity restriction."
          },
          {
            question: "What is the average hospital cost for a fall injury?",
            options: ["$10,000", "$20,000", "$30,000", "$40,000"],
            correctAnswer: "$30,000",
            explanation: "The average hospital cost for a fall injury exceeds $30,000, contributing to the significant financial burden of falls on the healthcare system."
          }
        ]
      }
    }
  },
  {
    id: "1.3", 
    title: "High-Risk Populations", 
    description: "Identify populations at higher risk for falls and learn targeted prevention strategies.", 
    duration: 15, 
    type: "Case Study", 
    content: {
      introduction: "While falls can affect anyone, certain populations face significantly higher risks due to specific conditions, age-related changes, or environmental circumstances. Understanding these high-risk groups allows for targeted prevention strategies that address their unique needs.",
      keyStats: [
        "Adults over 80 have a 50% higher fall risk than those aged 65-79",
        "80% of falls in healthcare settings involve patients with specific high-risk conditions",
        "Individuals with 3+ chronic conditions have a 60% higher fall risk",
        "Proper risk identification can reduce falls by up to 30% in high-risk groups"
      ],
      visualGuide: {
        component: "HighRiskFactorsGuide"
      },
      videoId: "high_risk_factors",
      videoCaption: "This video demonstrates the key risk factors for falls and how to identify them in various high-risk populations.",
      highRiskGroups: {
        title: "High-Risk Populations",
        groups: [
          {
            name: "Older Adults (65+)",
            riskFactors: [
              "Age-related muscle weakness and decreased flexibility",
              "Reduced reaction time and balance control",
              "Sensory changes (vision, proprioception, vestibular function)",
              "Higher prevalence of chronic conditions",
              "Medication side effects and interactions"
            ],
            preventionStrategies: [
              "Regular strength and balance exercises",
              "Comprehensive medication reviews",
              "Vision and hearing assessments",
              "Home safety modifications",
              "Use of appropriate assistive devices"
            ]
          },
          {
            name: "Individuals with Cognitive Impairment",
            riskFactors: [
              "Impaired judgment and risk awareness",
              "Difficulty following safety instructions",
              "Increased confusion in unfamiliar environments",
              "Wandering behaviors",
              "Side effects from psychotropic medications"
            ],
            preventionStrategies: [
              "Structured daily routines",
              "Environmental modifications with visual cues",
              "Supervised mobility assistance",
              "Medication optimization",
              "Regular toileting schedules to reduce urgency"
            ]
          },
          {
            name: "Post-Surgical Patients",
            riskFactors: [
              "Pain affecting mobility and gait",
              "Effects of anesthesia and pain medications",
              "Deconditioning from bed rest",
              "Unfamiliar environment",
              "Use of new assistive devices"
            ],
            preventionStrategies: [
              "Early mobilization with appropriate assistance",
              "Pain management optimization",
              "Clear instructions for transfers and mobility",
              "Environmental modifications in recovery area",
              "Proper training with assistive devices"
            ]
          },
          {
            name: "Individuals with Neurological Conditions",
            riskFactors: [
              "Impaired balance and coordination",
              "Muscle weakness or spasticity",
              "Sensory deficits",
              "Cognitive changes",
              "Medication side effects"
            ],
            preventionStrategies: [
              "Condition-specific exercise programs",
              "Adaptive equipment and assistive devices",
              "Environmental modifications",
              "Medication management",
              "Caregiver training for safe transfers and mobility assistance"
            ]
          }
        ]
      },
      
      caseStudies: {
        title: "Case Studies: Identifying Risk Factors in High-Risk Populations",
        studies: [
          {
            title: "Mrs. Johnson: Multiple Risk Factors",
            scenario: "Mrs. Johnson is a 78-year-old woman with mild cognitive impairment, osteoarthritis, and hypertension. She takes five different medications and lives alone in a two-story home. She has fallen twice in the past six months.",
            assessment: "Mrs. Johnson has multiple risk factors across different categories: age-related changes, cognitive impairment, chronic conditions, polypharmacy, and environmental risks (stairs). Her previous falls also significantly increase her risk of future falls.",
            interventions: [
              "Comprehensive medication review to identify potential interactions or medications increasing fall risk",
              "Home safety assessment with focus on stairway safety and frequently used pathways",
              "Physical therapy referral for strength and balance training",
              "Consider first-floor living arrangements or installation of stairlift",
              "Regular check-ins from family or caregivers"
            ]
          },
          {
            title: "Mr. Rivera: Post-Stroke Recovery",
            scenario: "Mr. Rivera is a 65-year-old man who recently returned home after hospitalization for a stroke. He has left-sided weakness, uses a quad cane, and is experiencing some cognitive changes. His bathroom has not been modified for his new mobility needs.",
            assessment: "Mr. Rivera's stroke has created multiple risk factors: muscle weakness, balance impairment, unfamiliarity with assistive device, cognitive changes, and an environment not adapted to his new needs.",
            interventions: [
              "Occupational therapy home assessment for bathroom and other environmental modifications",
              "Physical therapy for gait training with the quad cane",
              "Installation of grab bars, shower chair, and raised toilet seat",
              "Establishing clear pathways throughout the home",
              "Caregiver training for safe transfer techniques"
            ]
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check: High-Risk Populations",
        questions: [
          {
            question: "Which of the following is NOT typically a risk factor for falls in older adults?",
            options: [
              "Decreased muscle strength",
              "Improved reaction time",
              "Polypharmacy (taking multiple medications)",
              "Vision impairment"
            ],
            correctAnswer: "Improved reaction time",
            explanation: "Reaction time typically decreases with age, not improves. Decreased reaction time is a significant risk factor for falls in older adults as it reduces their ability to quickly respond to balance challenges."
          },
          {
            question: "For individuals with cognitive impairment, which fall prevention strategy is most appropriate?",
            options: [
              "Complex written instructions for safety",
              "Environmental modifications with visual cues",
              "Independent exercise programs",
              "Medication self-management"
            ],
            correctAnswer: "Environmental modifications with visual cues",
            explanation: "Environmental modifications with visual cues are most appropriate for individuals with cognitive impairment as they provide consistent, clear guidance without relying on memory or complex processing. Visual cues can help with orientation and safe navigation."
          },
          {
            question: "Which population has the highest risk of falling again after an initial fall?",
            options: [
              "Children under 10",
              "Adults aged 30-50",
              "Adults aged 50-65",
              "Adults over 65 who have already fallen once"
            ],
            correctAnswer: "Adults over 65 who have already fallen once",
            explanation: "Adults over 65 who have already fallen once have the highest risk of falling again. A previous fall is one of the strongest predictors of future falls, with the risk of a subsequent fall increasing by 2-3 times after an initial fall."
          }
        ]
      }
    }
  },
  {
    id: "1.4", 
    title: "Physical, Environmental, and Medication-Related Risks", 
    description: "Understand the three major categories of fall risk factors and how they interact to increase fall risk.", 
    duration: 20, 
    type: "Interactive", 
    content: {
      introduction: "Fall risk factors can be categorized into three main areas: physical factors related to the individual's health and abilities, environmental factors in the living space, and medication-related factors. Understanding how these factors interact is essential for comprehensive fall prevention.",
      keyStats: [
        "60% of falls involve multiple risk factors from different categories",
        "Environmental hazards contribute to about 30-50% of all falls",
        "Certain medications can increase fall risk by up to 140%",
        "Addressing risk factors across all three categories can reduce falls by up to 40%"
      ],
      videoId: "risk_categories",
      videoCaption: "This video explains the three major categories of fall risk factors and how they interact to increase overall fall risk.",
      visualGuide: {
        component: "RiskFactorsTriad"
      },
      riskAssessment: {
        title: "Comprehensive Risk Assessment Approach",
        description: "A thorough fall risk assessment should evaluate factors across all three categories. This multi-dimensional approach helps identify the most significant contributors to an individual's fall risk.",
        assessmentAreas: [
          {
            category: "Physical Assessment",
            elements: [
              "Gait and balance evaluation using standardized tools",
              "Muscle strength testing, particularly lower extremities",
              "Vision and hearing screening",
              "Evaluation of chronic conditions and their management",
              "Assessment of footwear and use of assistive devices"
            ]
          },
          {
            category: "Environmental Assessment",
            elements: [
              "Home safety checklist covering all living areas",
              "Lighting evaluation throughout the residence",
              "Bathroom safety features assessment",
              "Evaluation of furniture height and stability",
              "Assessment of floor surfaces and potential tripping hazards"
            ]
          },
          {
            category: "Medication Review",
            elements: [
              "Complete medication inventory including OTC and supplements",
              "Identification of high-risk medications",
              "Evaluation of medication timing and dosage",
              "Assessment of potential drug interactions",
              "Review of medication side effects and their management"
            ]
          }
        ]
      },
      interactionEffect: {
        title: "The Interaction Effect: When Risk Factors Multiply",
        description: "When risk factors from different categories are present simultaneously, they often create a multiplicative rather than additive effect on fall risk. Understanding these interactions is crucial for effective prevention.",
        examples: [
          {
            scenario: "Mrs. Chen takes a new blood pressure medication (medication factor) that causes mild dizziness when standing. She also has poor balance due to arthritis (physical factor) and has throw rugs in her hallway (environmental factor).",
            riskAnalysis: "Each factor alone presents a moderate risk, but together they create a high-risk situation. The medication-induced dizziness is more dangerous because of her existing balance problems, and both make the throw rugs a significant hazard.",
            interventionApproach: "A comprehensive approach would include medication adjustment, balance exercises, and environmental modifications—addressing all three risk categories simultaneously."
          },
          {
            scenario: "Mr. Jackson has Parkinson's disease affecting his gait (physical factor), takes multiple medications including a sedative for sleep (medication factor), and has poor lighting in his bathroom (environmental factor).",
            riskAnalysis: "The combination of an unsteady gait, sedating medication, and poor lighting creates a particularly dangerous situation for nighttime bathroom visits—a common time for falls.",
            interventionApproach: "Interventions should include gait training, medication review with possible alternatives for sleep, and improved lighting with motion sensors for nighttime bathroom visits."
          }
        ]
      },
      preventionStrategies: {
        title: "Integrated Prevention Strategies",
        strategies: [
          {
            category: "Physical Interventions",
            approaches: [
              "Tailored exercise programs focusing on strength, balance, and flexibility",
              "Physical therapy for specific mobility issues",
              "Management of chronic conditions",
              "Vision correction and regular eye exams",
              "Proper footwear and foot care"
            ]
          },
          {
            category: "Environmental Modifications",
            approaches: [
              "Removal of tripping hazards (throw rugs, clutter)",
              "Installation of grab bars and handrails",
              "Improved lighting throughout the home",
              "Bathroom safety modifications",
              "Furniture adjustments for safe transfers"
            ]
          },
          {
            category: "Medication Management",
            approaches: [
              "Regular medication reviews with healthcare providers",
              "Deprescribing unnecessary medications",
              "Adjusting dosages to minimize side effects",
              "Timing medications to avoid peak side effect periods during high-activity times",
              "Education about medication effects on fall risk"
            ]
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check: Risk Factor Categories",
        questions: [
          {
            question: "Which of the following best demonstrates the 'interaction effect' of fall risk factors?",
            options: [
              "An older adult with good vision who takes a sedative medication",
              "A person with balance problems who lives in a well-lit home with grab bars",
              "Someone taking multiple medications who also has poor vision and loose rugs at home",
              "A person with arthritis who uses a properly fitted walking cane"
            ],
            correctAnswer: "Someone taking multiple medications who also has poor vision and loose rugs at home",
            explanation: "This example demonstrates the interaction effect because it includes risk factors from all three categories (medication, physical, and environmental) that compound each other's effects. The poor vision (physical) makes it harder to see the loose rugs (environmental), and the medications may cause side effects that further impair balance and reaction time."
          },
          {
            question: "Which medication category is most commonly associated with increased fall risk?",
            options: [
              "Antibiotics",
              "Psychotropics (antidepressants, sedatives, antipsychotics)",
              "Antacids",
              "Vitamins"
            ],
            correctAnswer: "Psychotropics (antidepressants, sedatives, antipsychotics)",
            explanation: "Psychotropic medications including antidepressants, sedatives, and antipsychotics are most commonly associated with increased fall risk. These medications can cause side effects such as dizziness, sedation, orthostatic hypotension, and impaired balance, all of which significantly increase fall risk."
          },
          {
            question: "When conducting a fall risk assessment, why is it important to evaluate all three risk categories?",
            options: [
              "Because it's the standard protocol in healthcare settings",
              "To make the assessment process longer and more thorough",
              "Because most falls are caused by a single factor that must be identified",
              "Because risk factors interact across categories to multiply overall fall risk"
            ],
            correctAnswer: "Because risk factors interact across categories to multiply overall fall risk",
            explanation: "It's essential to evaluate all three risk categories because risk factors interact across these categories to multiply overall fall risk. A comprehensive assessment allows for identification of these interactions and development of a more effective, multi-faceted prevention plan that addresses all contributing factors."
          }
        ]
      }
    }
  },
  {
    id: "1.5", 
    title: "Psychological and Emotional Impact of Falls", 
    description: "Understand how falls affect mental health, confidence, and quality of life, and learn strategies to address these impacts.", 
    duration: 20, 
    type: "Interactive", 
    content: {
      introduction: "The impact of falls extends far beyond physical injuries. The psychological and emotional consequences can be equally debilitating and often persist long after physical recovery. Understanding these effects is crucial for providing holistic care and effective fall prevention.",
      keyStats: [
        "Up to 70% of older adults who have fallen develop a fear of falling",
        "Fear of falling leads to activity restriction in 50% of those affected",
        "25-30% of older adults develop depression after a fall",
        "40% of nursing home admissions follow a fall event"
      ],
      visualGuide: {
        component: "EmotionalImpactVisual"
      },
      videoId: "psychological_impact",
      videoCaption: "This video illustrates the psychological and emotional impacts that falls can have on patients and strategies to address these effects.",
      psychologicalEffects: {
        title: "Key Psychological Effects of Falls",
        effects: [
          {
            effect: "Fear of Falling",
            description: "Fear of falling is one of the most common psychological consequences of falls, affecting up to 70% of older adults who have fallen. This fear can develop even in individuals who have not experienced a fall but have witnessed others falling or have been cautioned about fall risks.",
            consequences: [
              "Activity restriction and self-imposed limitations",
              "Decreased physical function due to reduced activity",
              "Social isolation and withdrawal from activities",
              "Increased risk of future falls due to deconditioning",
              "Decreased quality of life and independence"
            ],
            interventions: [
              "Cognitive-behavioral therapy to address irrational fears",
              "Gradual exposure to feared activities with appropriate support",
              "Balance confidence training through supervised exercise",
              "Education about actual vs. perceived risk",
              "Fall prevention education to increase sense of control"
            ]
          },
          {
            effect: "Depression and Anxiety",
            description: "Falls can trigger or exacerbate depression and anxiety, particularly when they result in functional limitations, pain, or changes in living situation. The prevalence of depression following falls ranges from 25-30% in community-dwelling older adults and can be even higher in those requiring hospitalization.",
            consequences: [
              "Decreased motivation for rehabilitation",
              "Reduced adherence to fall prevention strategies",
              "Increased risk of cognitive decline",
              "Impaired sleep and appetite",
              "Diminished quality of life"
            ],
            interventions: [
              "Screening for depression and anxiety following falls",
              "Referral to mental health professionals when indicated",
              "Social support and peer group activities",
              "Meaningful activity engagement to restore purpose",
              "Medication management for severe symptoms"
            ]
          },
          {
            effect: "Loss of Confidence and Self-Efficacy",
            description: "Falls often lead to a significant loss of confidence in one's abilities and a decreased sense of self-efficacy. This can affect not only mobility-related activities but also general self-perception and identity.",
            consequences: [
              "Reluctance to attempt previously routine activities",
              "Increased dependence on caregivers",
              "Altered self-perception and identity",
              "Reduced sense of autonomy and control",
              "Diminished quality of life"
            ],
            interventions: [
              "Setting and achieving progressive goals to rebuild confidence",
              "Positive reinforcement of capabilities and achievements",
              "Skill-building in safe mobility techniques",
              "Assistive devices as confidence boosters rather than symbols of disability",
              "Peer support from others who have overcome similar challenges"
            ]
          }
        ]
      },
      impactCycle: {
        title: "The Psychological Impact Cycle",
        description: "Psychological impacts of falls often create a self-reinforcing negative cycle that can be difficult to break without intervention:",
        stages: [
          {
            stage: "1. Fall Event or Near Miss",
            description: "A fall or near-fall experience creates initial fear and anxiety."
          },
          {
            stage: "2. Fear Development",
            description: "Fear of falling develops or intensifies, often disproportionate to actual risk."
          },
          {
            stage: "3. Activity Restriction",
            description: "Individual begins limiting activities to avoid situations perceived as risky."
          },
          {
            stage: "4. Physical Deconditioning",
            description: "Reduced activity leads to muscle weakness, decreased balance, and loss of flexibility."
          },
          {
            stage: "5. Increased Actual Fall Risk",
            description: "Physical deconditioning increases the actual risk of falling."
          },
          {
            stage: "6. Reinforced Fear",
            description: "Increased unsteadiness reinforces fear, leading to further activity restriction."
          }
        ],
        interventionPoints: [
          "Breaking the cycle requires interventions at multiple points simultaneously",
          "Physical exercise to improve strength and balance",
          "Psychological interventions to address fear and build confidence",
          "Environmental modifications to increase safety and reduce anxiety",
          "Social support to encourage activity participation"
        ]
      },
      caregiverStrategies: {
        title: "Caregiver Strategies for Addressing Psychological Impact",
        strategies: [
          {
            strategy: "Validate Emotions",
            approaches: [
              "Acknowledge that fear and anxiety are normal responses",
              "Listen empathetically without dismissing concerns",
              "Avoid phrases like 'don't worry' or 'you're fine'",
              "Recognize the real impact of psychological distress"
            ]
          },
          {
            strategy: "Promote Gradual Confidence Building",
            approaches: [
              "Start with activities that have minimal fall risk",
              "Provide appropriate levels of assistance that promote independence",
              "Celebrate small achievements and progress",
              "Use positive, encouraging language",
              "Focus on capabilities rather than limitations"
            ]
          },
          {
            strategy: "Facilitate Social Engagement",
            approaches: [
              "Encourage participation in group activities",
              "Connect individuals with peers who have similar experiences",
              "Promote family involvement in confidence-building activities",
              "Consider technology options for social connection when mobility is limited"
            ]
          },
          {
            strategy: "Provide Education and Resources",
            approaches: [
              "Teach about the psychological impact cycle and how to break it",
              "Provide information about available community resources",
              "Connect with mental health professionals when needed",
              "Share success stories of others who have overcome fear of falling"
            ]
          }
        ]
      },
      caseStudy: {
        title: "Case Study: Addressing the Psychological Impact of Falls",
        scenario: "Mrs. Garcia, a 72-year-old woman, fell in her bathroom three months ago, resulting in a wrist fracture. Although the fracture has healed well, she has developed significant fear of falling and has restricted her activities. She no longer attends her weekly card game with friends, has stopped gardening (a beloved hobby), and is reluctant to leave her home without assistance. Her daughter has noticed signs of depression, including loss of interest in activities and decreased appetite.",
        assessment: "Mrs. Garcia is experiencing classic post-fall psychological effects: fear of falling, activity restriction, social withdrawal, and depression. Her physical recovery has been good, but her psychological recovery is lagging, creating risk for the negative cycle of deconditioning and increased fall risk.",
        interventions: [
          "Physical therapy focused on balance confidence and safe mobility techniques",
          "Cognitive-behavioral therapy to address fear and depression",
          "Gradual reintroduction to gardening with adaptive tools and techniques",
          "Bathroom modifications to increase safety and reduce anxiety",
          "Social prescription to a falls prevention and confidence building group",
          "Family education about supporting independence while ensuring safety"
        ],
        outcomes: "After three months of intervention, Mrs. Garcia reported a 60% reduction in fear of falling, resumed attending her card game with friends, began container gardening as a modification of her previous hobby, and showed significant improvement in depression symptoms. Her physical function improved as activity levels increased, creating a positive cycle of confidence and capability."
      },
      knowledgeCheck: {
        title: "Knowledge Check: Psychological Impact of Falls",
        questions: [
          {
            question: "Which of the following best describes the 'psychological impact cycle' of falls?",
            options: [
              "Falls cause depression, which leads to medication use, which increases fall risk",
              "Fear leads to activity restriction, which causes physical deconditioning, which increases actual fall risk",
              "Falls cause social isolation, which leads to cognitive decline, which increases fall risk",
              "Anxiety causes poor sleep, which leads to fatigue, which increases fall risk"
            ],
            correctAnswer: "Fear leads to activity restriction, which causes physical deconditioning, which increases actual fall risk",
            explanation: "The psychological impact cycle describes how fear of falling leads to self-imposed activity restriction, which results in physical deconditioning (muscle weakness, decreased balance). This deconditioning actually increases the real risk of falling, which reinforces the fear, continuing the cycle."
          },
          {
            question: "What percentage of older adults develop fear of falling after experiencing a fall?",
            options: [
              "Up to 30%",
              "Up to 50%",
              "Up to 70%",
              "Up to 90%"
            ],
            correctAnswer: "Up to 70%",
            explanation: "Research indicates that up to 70% of older adults who have experienced a fall develop fear of falling. This fear can be as debilitating as physical injuries and often persists long after physical recovery."
          },
          {
            question: "Which approach is most effective for addressing the psychological impact of falls?",
            options: [
              "Restricting activities to prevent future falls",
              "Focusing exclusively on physical rehabilitation",
              "Using multiple interventions that address both physical and psychological aspects simultaneously",
              "Prescribing anti-anxiety medications"
            ],
            correctAnswer: "Using multiple interventions that address both physical and psychological aspects simultaneously",
            explanation: "The most effective approach for addressing the psychological impact of falls is to use multiple interventions that simultaneously address both physical and psychological aspects. This might include physical therapy for strength and balance, cognitive-behavioral therapy for fear, environmental modifications for safety, and social engagement for emotional well-being."
          }
        ]
      }
    }
  },
];

export const module2_lessons = [
  { isHeader: true, title: 'Risk Assessment and Prevention Strategies' },
  {
    id: "2.1",
    title: "Conducting a Fall Risk Assessment",
    description: "Learn how to conduct a thorough assessment of fall risk factors using standardized tools and methods.",
    duration: 25,
    type: "Interactive Lesson",
    content: {
      introduction: "A comprehensive fall risk assessment is the foundation of effective fall prevention. This systematic approach helps identify individual risk factors and guides targeted interventions.",
      keyStats: [
        "Proper risk assessment can reduce fall incidents by up to 24%",
        "Over 400 risk factors for falls have been identified in research",
        "Combination of assessment methods yields the highest predictive accuracy",
        "Regular reassessment is critical as fall risk can change rapidly"
      ],
      videoId: "conducting_assessment",
      videoCaption: "This video demonstrates how to conduct a comprehensive fall risk assessment using standardized tools and methods.",
      assessmentTools: {
        title: "Standardized Assessment Tools",
        description: "These validated tools provide structured approaches to fall risk evaluation:",
        tools: [
          {
            name: "Morse Fall Scale (MFS)",
            setting: "Acute care, hospitals",
            components: "History of falling, secondary diagnosis, ambulatory aid, IV/heparin lock, gait/transferring, mental status",
            scoring: "0-125 points; ≥45 indicates high risk",
            advantages: "Quick to administer (2-3 minutes), high sensitivity"
          },
          {
            name: "Timed Up and Go (TUG) Test",
            setting: "Community, outpatient, long-term care",
            components: "Timed assessment of rising from chair, walking 3 meters, turning, returning to chair",
            scoring: ">12 seconds indicates increased fall risk",
            advantages: "No special equipment needed, assesses functional mobility"
          },
          {
            name: "Berg Balance Scale",
            setting: "Rehabilitation, outpatient",
            components: "14 items testing balance abilities of varying difficulty",
            scoring: "0-56 points; <45 indicates increased fall risk",
            advantages: "Comprehensive balance assessment, good predictive validity"
          },
          {
            name: "STRATIFY (St. Thomas Risk Assessment Tool)",
            setting: "Acute care, hospitals",
            components: "Prior falls, agitation, visual impairment, frequent toileting, transfer/mobility",
            scoring: "0-5 points; ≥2 indicates high risk",
            advantages: "Simple to use, specifically designed for hospital settings"
          }
        ]
      },
      assessmentDomains: {
        title: "Key Assessment Domains",
        domains: [
          {
            domain: "Medical History",
            elements: [
              "Previous falls (circumstances, frequency, injuries)",
              "Chronic conditions (arthritis, Parkinson's, stroke, etc.)",
              "Acute illnesses or recent hospitalizations",
              "Medication review (polypharmacy, high-risk medications)",
              "Sensory deficits (vision, hearing, proprioception)"
            ]
          },
          {
            domain: "Physical Assessment",
            elements: [
              "Gait and balance evaluation",
              "Muscle strength (particularly lower extremities)",
              "Range of motion and flexibility",
              "Postural hypotension (blood pressure lying/standing)",
              "Footwear and foot problems"
            ]
          },
          {
            domain: "Functional Assessment",
            elements: [
              "Activities of daily living (ADLs)",
              "Use of assistive devices",
              "Transfer abilities",
              "Stair navigation",
              "Endurance and activity level"
            ]
          },
          {
            domain: "Environmental Assessment",
            elements: [
              "Home hazards evaluation",
              "Lighting adequacy",
              "Bathroom safety features",
              "Furniture arrangement and stability",
              "Floor surfaces and transitions"
            ]
          },
          {
            domain: "Cognitive/Behavioral Assessment",
            elements: [
              "Cognitive status and awareness",
              "Fear of falling",
              "Risk-taking behaviors",
              "Medication adherence",
              "Understanding of safety precautions"
            ]
          }
        ]
      },
      assessmentProcess: {
        title: "Structured Assessment Process",
        steps: [
          {
            step: "1. Initial Screening",
            description: "Quickly identify individuals who need comprehensive assessment using brief screening tools"
          },
          {
            step: "2. Comprehensive Assessment",
            description: "Perform detailed evaluation across all risk domains for those identified in screening"
          },
          {
            step: "3. Risk Stratification",
            description: "Categorize individuals into risk levels (low, moderate, high) based on assessment findings"
          },
          {
            step: "4. Intervention Planning",
            description: "Develop targeted interventions addressing identified risk factors"
          },
          {
            step: "5. Documentation",
            description: "Record assessment findings, risk level, and intervention plan in the care record"
          },
          {
            step: "6. Communication",
            description: "Share assessment results with the care team, patient, and family members"
          },
          {
            step: "7. Reassessment",
            description: "Schedule regular reassessments and after significant changes in condition"
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of fall risk assessment",
        questions: [
          {
            question: "Which assessment tool is most appropriate for quick screening in a hospital setting?",
            options: ["Berg Balance Scale", "Timed Up and Go Test", "Morse Fall Scale", "Functional Reach Test"],
            correctAnswer: "Morse Fall Scale",
            explanation: "The Morse Fall Scale is designed specifically for hospital settings and can be administered quickly (2-3 minutes)."
          },
          {
            question: "What score on the Timed Up and Go (TUG) test indicates increased fall risk?",
            options: [">5 seconds", ">8 seconds", ">12 seconds", ">20 seconds"],
            correctAnswer: ">12 seconds",
            explanation: "A TUG test time greater than 12 seconds is generally considered to indicate increased fall risk in community-dwelling older adults."
          },
          {
            question: "Which is NOT one of the key assessment domains for fall risk evaluation?",
            options: ["Medical history", "Physical assessment", "Financial resources", "Environmental assessment"],
            correctAnswer: "Financial resources",
            explanation: "While financial resources may affect access to interventions, it is not one of the standard domains assessed in fall risk evaluation."
          }
        ]
      }
    }
  },
  {
    id: "2.2",
    title: "Identifying and Eliminating Environmental Hazards",
    description: "Learn to identify and address common environmental risk factors for falls.",
    duration: 20,
    type: "Interactive Lesson",
    content: {
      introduction: "Environmental hazards are a significant contributor to falls, particularly in home settings. Identifying and eliminating these hazards is one of the most effective and straightforward fall prevention strategies.",
      keyStats: [
        "Environmental factors contribute to approximately 30-50% of falls",
        "Home safety modifications can reduce fall risk by up to 39%",
        "Most homes have at least 3-5 significant fall hazards",
        "Simple modifications often cost less than $200 but can prevent costly injuries"
      ],
      videoId: "environmental_hazards",
      videoCaption: "This video demonstrates how to identify and eliminate common environmental hazards that contribute to falls.",
      commonHazards: {
        title: "Common Environmental Hazards",
        areas: [
          {
            area: "Floors and Walkways",
            hazards: [
              "Loose or unsecured rugs and mats",
              "Cluttered walkways and high-traffic areas",
              "Electrical cords across pathways",
              "Uneven flooring or thresholds between rooms",
              "Highly polished or slippery floors",
              "Poor contrast between floor levels or surfaces"
            ]
          },
          {
            area: "Stairs and Steps",
            hazards: [
              "Lack of handrails or unstable handrails",
              "Poor lighting on stairways",
              "Lack of contrast on step edges",
              "Uneven step heights or depths",
              "Cluttered stairs",
              "Loose carpeting on stairs"
            ]
          },
          {
            area: "Bathrooms",
            hazards: [
              "Lack of grab bars near toilet and shower/tub",
              "Slippery surfaces in tub or shower",
              "Low toilet seats",
              "Unsecured bath mats",
              "Poor lighting",
              "Difficult access to toiletries and towels"
            ]
          },
          {
            area: "Bedroom",
            hazards: [
              "Inadequate lighting for nighttime bathroom trips",
              "Beds at inappropriate height (too high or too low)",
              "Cluttered pathways to bathroom",
              "Unstable furniture used for support",
              "Lack of nightlights",
              "Telephone or call system out of reach"
            ]
          },
          {
            area: "Kitchen",
            hazards: [
              "Items stored out of easy reach (too high or too low)",
              "Slippery floors",
              "Poor lighting in work areas",
              "Unstable step stools",
              "Lack of stable work surfaces for seated tasks",
              "Spills not promptly cleaned"
            ]
          }
        ]
      },
      assessmentProcess: {
        title: "Environmental Assessment Process",
        steps: [
          {
            step: "Systematic Room-by-Room Inspection",
            description: "Evaluate each room methodically using a standardized checklist"
          },
          {
            step: "Observe Patient Mobility Patterns",
            description: "Watch how the individual moves through their environment to identify specific challenges"
          },
          {
            step: "Identify High-Risk Areas",
            description: "Focus on areas where falls commonly occur: bathrooms, stairs, and transitions between rooms"
          },
          {
            step: "Evaluate Lighting Conditions",
            description: "Check lighting at different times of day, especially during evening/night hours"
          },
          {
            step: "Assess Furniture Stability and Arrangement",
            description: "Ensure furniture is stable and arranged to facilitate safe movement"
          },
          {
            step: "Review Assistive Device Compatibility",
            description: "Ensure the environment accommodates walkers, wheelchairs, or other assistive devices"
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of environmental hazards",
        questions: [
          {
            question: "Which room in the home typically has the highest fall risk?",
            options: ["Living room", "Kitchen", "Bathroom", "Bedroom"],
            correctAnswer: "Bathroom",
            explanation: "Bathrooms combine multiple high-risk elements: slippery surfaces, transfers (to/from toilet, tub), confined spaces, and activities requiring balance."
          },
          {
            question: "Which environmental modification is LEAST effective for fall prevention?",
            options: ["Installing grab bars in bathrooms", "Removing throw rugs", "Changing wall colors", "Improving lighting in hallways"],
            correctAnswer: "Changing wall colors",
            explanation: "While contrast between walls and floors can be helpful, simply changing wall colors without addressing actual hazards like poor lighting or tripping hazards has minimal impact."
          },
          {
            question: "What percentage of falls involve environmental factors as contributors?",
            options: ["5-10%", "15-25%", "30-50%", "60-80%"],
            correctAnswer: "30-50%",
            explanation: "Research indicates that environmental factors contribute to approximately 30-50% of falls, making environmental modification a high-impact intervention."
          }
        ]
      }
    }
  },
  {
    id: "2.3",
    title: "Medication Review",
    description: "Learn how to identify medications that increase fall risk.",
    duration: 20,
    type: "Interactive Lesson",
    content: {
      introduction: "Medication review is a critical component of fall risk assessment. Many commonly prescribed medications can increase the risk of falls through side effects such as dizziness, sedation, orthostatic hypotension, vision changes, or altered balance and coordination.",
      statistics: [
        "Patients taking four or more medications have a 2-3 times greater fall risk",
        "Psychotropic medications can increase fall risk by up to 47%",
        "Nearly 40% of falls among older adults involve medication as a contributing factor",
        "Medication review and adjustment can reduce fall rates by up to 39%"
      ],
      videoId: "medication_review",
      videoCaption: "This video demonstrates how to conduct a thorough medication review to identify medications that increase fall risk.",
      highRiskMedications: {
        title: "High-Risk Medication Categories",
        description: "The following medication types are commonly associated with increased fall risk:",
        categories: [
          {
            category: "Psychotropics",
            examples: "Antidepressants, antipsychotics, benzodiazepines, sedative-hypnotics, anxiolytics",
            mechanism: "Cause sedation, cognitive impairment, orthostatic hypotension, and balance problems",
            recommendations: "Consider dose reduction, alternative therapies, or gradual discontinuation when appropriate"
          },
          {
            category: "Cardiovascular Medications",
            examples: "Antihypertensives, diuretics, beta-blockers, ACE inhibitors, nitrates",
            mechanism: "May cause orthostatic hypotension, dehydration, electrolyte imbalances, or syncope",
            recommendations: "Monitor blood pressure in both lying and standing positions; adjust doses to minimize orthostatic changes"
          },
          {
            category: "Anticonvulsants",
            examples: "Phenytoin, carbamazepine, gabapentin, pregabalin",
            mechanism: "Can cause ataxia, dizziness, and impaired coordination",
            recommendations: "Monitor for side effects closely; consider alternative agents if balance issues develop"
          },
          {
            category: "Opioid Analgesics",
            examples: "Morphine, oxycodone, hydrocodone, tramadol",
            mechanism: "Cause sedation, cognitive impairment, and balance disturbances",
            recommendations: "Use lowest effective dose; consider non-opioid alternatives when possible"
          },
          {
            category: "Anticholinergics",
            examples: "First-generation antihistamines, bladder antimuscarinics, tricyclic antidepressants",
            mechanism: "Cause blurred vision, confusion, and cognitive impairment",
            recommendations: "Avoid in older adults when possible; use alternatives with lower anticholinergic burden"
          }
        ]
      },
      medicationReviewProcess: {
        title: "Structured Medication Review Process",
        steps: [
          {
            step: "Collect Comprehensive Medication List",
            details: "Document all prescription medications, over-the-counter products, supplements, and herbal remedies"
          },
          {
            step: "Identify High-Risk Medications",
            details: "Flag medications known to increase fall risk based on established criteria"
          },
          {
            step: "Evaluate Appropriateness",
            details: "Assess indication, effectiveness, dosage, and continued need for each medication"
          },
          {
            step: "Review Timing and Administration",
            details: "Consider medication timing, interactions, and administration challenges"
          },
          {
            step: "Develop Modification Plan",
            details: "Collaborate with prescribers on potential changes to reduce fall risk"
          },
          {
            step: "Monitor and Follow Up",
            details: "Track changes in symptoms, side effects, and fall risk after modifications"
          }
        ]
      },
      interventionStrategies: {
        title: "Medication Intervention Strategies",
        strategies: [
          "Deprescribing: Systematically withdrawing inappropriate medications",
          "Dose adjustment: Reducing doses to minimize side effects while maintaining benefits",
          "Therapeutic substitution: Switching to safer alternatives within the same class",
          "Timing adjustment: Changing administration times to minimize peak side effects",
          "Medication reconciliation: Eliminating duplicate therapies and unnecessary medications",
          "Non-pharmacological alternatives: Suggesting evidence-based non-drug approaches"
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of medication review for fall prevention",
        questions: [
          {
            question: "Which medication class has the strongest association with increased fall risk?",
            options: ["Antibiotics", "Psychotropics", "Vitamins", "Thyroid medications"],
            correctAnswer: "Psychotropics",
            explanation: "Psychotropic medications (antidepressants, antipsychotics, benzodiazepines, etc.) significantly increase fall risk through multiple mechanisms including sedation and balance impairment."
          },
          {
            question: "How many medications typically constitute polypharmacy with increased fall risk?",
            options: ["2 or more", "4 or more", "10 or more", "Any number of medications"],
            correctAnswer: "4 or more",
            explanation: "Taking four or more medications (polypharmacy) is associated with a 2-3 times greater risk of falls."
          },
          {
            question: "Which is NOT a recommended step in the medication review process?",
            options: ["Collecting a comprehensive medication list", "Immediately stopping all high-risk medications", "Evaluating medication appropriateness", "Developing a modification plan"],
            correctAnswer: "Immediately stopping all high-risk medications",
            explanation: "Abruptly stopping medications can cause withdrawal effects or symptom recurrence. A careful, planned approach with prescriber collaboration is essential."
          }
        ]
      }
    }
  },
  {
    id: "2.4",
    title: "Proper Use of Assistive Devices",
    description: "Learn about various assistive devices for mobility and how to ensure their proper use to prevent falls.",
    duration: 25,
    
    content: {
      introduction: "Assistive devices can significantly enhance mobility and independence while reducing fall risk when properly selected, fitted, and used. However, improper use can actually increase fall risk, making proper training and ongoing assessment essential.",
      keyStats: [
        "Properly fitted walking aids can reduce fall risk by up to 60%",
        "47% of older adults use assistive devices incorrectly, increasing fall risk",
        "Regular reassessment of assistive devices can reduce falls by 30%",
        "Over 35% of falls involve improper use of or inappropriate assistive devices"
      ],
      commonDevices: {
        title: "Common Assistive Devices",
        devices: [
          {
            name: "Standard Walker",
            description: "Four-legged frame without wheels that must be lifted for each step",
            bestFor: "Individuals needing maximum stability who have adequate upper body strength",
            considerations: "Requires lifting, which can be difficult for some; slower mobility; not suitable for uneven surfaces",
            properUse: [
              "Stand inside the walker, not behind it",
              "Adjust height so elbows are at approximately 30° angle when hands are on grips",
              "Lift walker forward, then step into it",
              "Ensure all four legs contact the ground before bearing weight"
            ]
          },
          {
            name: "Rolling Walker (2-Wheel)",
            description: "Walker with wheels on front legs, back legs have rubber tips",
            bestFor: "Individuals who have difficulty lifting a standard walker but still need significant stability",
            considerations: "Easier to move than standard walker; moderate stability; requires some control to prevent rolling too quickly",
            properUse: [
              "Stand inside the walker with good posture",
              "Roll the walker forward rather than lifting it",
              "Take steps into the walker, not around it",
              "Apply downward pressure for stability when needed"
            ]
          },
          {
            name: "Rollator (4-Wheel)",
            description: "Walker with four wheels, hand brakes, and usually a seat",
            bestFor: "Individuals who need support but have reasonable balance and need to walk longer distances",
            considerations: "Provides less stability than standard walkers; requires hand strength for brakes; allows for rest periods with seat",
            properUse: [
              "Ensure brakes are functioning before each use",
              "Use brakes when standing from seated position",
              "Maintain an upright posture, avoiding leaning forward",
              "Lock brakes before sitting on the seat",
              "Adjust to proper height with wrists level with grips when standing inside"
            ]
          },
          {
            name: "Quad Cane",
            description: "Cane with four-point base for increased stability",
            bestFor: "Individuals needing moderate support on one side",
            considerations: "More stable than standard cane; heavier; wider base requires attention to placement",
            properUse: [
              "Hold on side opposite to weakness/injury",
              "Move cane forward simultaneously with the weaker/affected leg",
              "Ensure all four points contact the ground",
              "Adjust height so elbow is bent approximately 30°"
            ]
          },
          {
            name: "Standard Cane",
            description: "Single-point cane, often with curved handle",
            bestFor: "Individuals needing minimal support or balance assistance",
            considerations: "Least stable option; lightest weight; easiest to maneuver in tight spaces",
            properUse: [
              "Hold on side opposite to weakness/injury",
              "Move cane forward with opposite leg",
              "Adjust height so elbow is bent approximately 30°",
              "Ensure rubber tip is intact and not worn"
            ]
          },
          {
            name: "Crutches",
            description: "Underarm or forearm supports for significant weight-bearing restrictions",
            bestFor: "Individuals who cannot bear full weight on one or both legs",
            considerations: "Require significant upper body strength and coordination; different gait patterns based on weight-bearing status",
            properUse: [
              "Ensure proper height (2-3 finger widths below armpit for underarm crutches)",
              "Bear weight on hands, not underarms, to prevent nerve damage",
              "Follow prescribed gait pattern (3-point, 4-point, swing-to, swing-through)",
              "Maintain proper posture with shoulders relaxed"
            ]
          }
        ]
      },
      selectionProcess: {
        title: "Selection and Fitting Process",
        steps: [
          {
            step: "Assessment of Needs",
            details: "Evaluate mobility limitations, balance, strength, cognitive status, and home environment"
          },
          {
            step: "Device Selection",
            details: "Choose appropriate device based on stability needs, functional abilities, and environmental considerations"
          },
          {
            step: "Proper Measurement",
            details: "Ensure correct height and size for user's body dimensions"
          },
          {
            step: "Initial Training",
            details: "Provide comprehensive instruction on proper use, including specific gait patterns"
          },
          {
            step: "Environmental Adaptation",
            details: "Assess and modify home environment to accommodate device use"
          },
          {
            step: "Regular Reassessment",
            details: "Schedule periodic evaluations to ensure continued appropriateness and proper use"
          }
        ]
      },
      commonErrors: {
        title: "Common Errors and Corrections",
        errors: [
          {
            error: "Improper Height Adjustment",
            consequences: "Excessive bending, shoulder elevation, poor posture, increased fall risk",
            correction: "Adjust device so elbow is bent approximately 30° when standing with good posture"
          },
          {
            error: "Poor Posture/Leaning",
            consequences: "Back pain, reduced stability, altered gait mechanics",
            correction: "Practice maintaining upright posture; consider physical therapy for core strengthening"
          },
          {
            error: "Incorrect Gait Pattern",
            consequences: "Reduced stability, inefficient movement, increased energy expenditure",
            correction: "Review and practice proper sequencing with healthcare provider"
          },
          {
            error: "Improper Placement",
            consequences: "Device too far ahead or behind, causing reaching or instability",
            correction: "Place device at appropriate distance to maintain center of gravity within base of support"
          },
          {
            error: "Using Worn Equipment",
            consequences: "Reduced stability, equipment failure, falls",
            correction: "Regularly inspect tips, wheels, brakes; replace worn components"
          }
        ]
      },
      trainingProtocol: {
        title: "Comprehensive Training Protocol",
        components: [
          {
            component: "Initial Demonstration",
            details: "Healthcare provider demonstrates proper use with clear explanation"
          },
          {
            component: "Supervised Practice",
            details: "User practices with close supervision in controlled environment"
          },
          {
            component: "Environmental Challenges",
            details: "Gradually introduce real-world challenges (different surfaces, doorways, etc.)"
          },
          {
            component: "Return Demonstration",
            details: "User demonstrates proper use independently"
          },
          {
            component: "Family/Caregiver Training",
            details: "Include support persons in training to ensure consistent assistance"
          },
          {
            component: "Written Instructions",
            details: "Provide clear written/visual materials for reference"
          },
          {
            component: "Follow-up Assessment",
            details: "Schedule check-in to evaluate continued proper use and address questions"
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of assistive devices",
        questions: [
          {
            question: "Which assistive device provides the most stability?",
            options: ["Standard cane", "Quad cane", "Rollator (4-wheel walker)", "Standard walker"],
            correctAnswer: "Standard walker",
            explanation: "A standard walker provides the most stability due to its wide base of support and four-point contact with the ground. It requires lifting for each step, which slows movement but maximizes stability."
          },
          {
            question: "What is the correct height for a properly fitted cane?",
            options: ["At hip level", "At shoulder level", "Elbow bent at approximately 30°", "Wrist level when arm is fully extended"],
            correctAnswer: "Elbow bent at approximately 30°",
            explanation: "The correct height for a cane results in approximately 30° of elbow flexion when the cane tip is placed 6 inches lateral to the small toe. This allows for proper weight distribution and biomechanics."
          },
          {
            question: "Which of the following is NOT a component of proper walker use?",
            options: ["Lifting the walker completely off the ground", "Standing inside the walker", "Moving the walker far ahead before stepping", "Ensuring all legs contact the ground before bearing weight"],
            correctAnswer: "Moving the walker far ahead before stepping",
            explanation: "Moving the walker too far ahead shifts the user's center of gravity forward outside the base of support, increasing fall risk. The walker should be moved a comfortable distance (about 6-10 inches) ahead."
          }
        ]
      }
    }
  },
  {
    id: "2.5",
    title: "Standard Assessment Tests",
    description: "Learn about validated assessment tools and tests used to evaluate fall risk in different populations.",
    duration: 30,
    type: "Interactive Lesson",
    content: {
      introduction: "Standardized assessment tests provide objective, validated methods for evaluating fall risk. These tools help clinicians identify specific risk factors, quantify overall risk levels, and track changes over time, enabling more targeted and effective interventions.",
      keyStats: [
        "Using standardized assessment tools can improve fall prediction accuracy by up to 70%",
        "Combining multiple assessment tools increases sensitivity to 91% and specificity to 82%",
        "Regular reassessment using standardized tools can reduce falls by up to 24%",
        "Documentation using validated tools improves care coordination across healthcare settings"
      ],
      videoId: "standard_assessment",
      videoCaption: "This video demonstrates how to properly administer and interpret standard assessment tests for fall risk evaluation.",
      assessmentCategories: {
        title: "Categories of Fall Risk Assessment",
        categories: [
          {
            name: "Balance Assessment",
            purpose: "Evaluate static and dynamic balance abilities",
            examples: ["Berg Balance Scale", "Tinetti Performance Oriented Mobility Assessment", "Functional Reach Test"]
          },
          {
            name: "Gait Assessment",
            purpose: "Analyze walking patterns and mobility",
            examples: ["Timed Up and Go (TUG)", "Dynamic Gait Index", "Tinetti Gait Assessment"]
          },
          {
            name: "Strength Assessment",
            purpose: "Measure muscle strength, particularly in lower extremities",
            examples: ["Five Times Sit-to-Stand Test", "Manual Muscle Testing", "Handheld Dynamometry"]
          },
          {
            name: "Multifactorial Assessment",
            purpose: "Comprehensive evaluation of multiple risk factors",
            examples: ["Morse Fall Scale", "STRATIFY", "Hendrich II Fall Risk Model"]
          },
          {
            name: "Cognitive Assessment",
            purpose: "Evaluate cognitive factors that may contribute to falls",
            examples: ["Mini-Mental State Examination", "Montreal Cognitive Assessment", "Trail Making Test"]
          }
        ]
      },
      commonTests: {
        title: "Commonly Used Assessment Tests",
        tests: [
          {
            name: "Timed Up and Go (TUG)",
            purpose: "Assess mobility, balance, and fall risk",
            procedure: "Time how long it takes a person to rise from a chair, walk 3 meters, turn around, walk back, and sit down",
            scoring: ">12 seconds indicates increased fall risk in community-dwelling older adults; >20 seconds indicates high risk",
            strengths: "Quick, requires minimal equipment, good predictor of falls in community-dwelling elderly",
            limitations: "Less sensitive for hospitalized patients or those with cognitive impairments"
          },
          {
            name: "Berg Balance Scale",
            purpose: "Assess balance through 14 different tasks",
            procedure: "Patient performs 14 functional tasks of increasing difficulty, each scored 0-4",
            scoring: "Total score 0-56; <45 indicates increased fall risk; <36 indicates high risk (nearly 100% probability)",
            strengths: "Comprehensive, well-validated, assesses multiple aspects of balance",
            limitations: "Takes 15-20 minutes to complete, requires training to administer properly"
          },
          {
            name: "Tinetti Performance Oriented Mobility Assessment (POMA)",
            purpose: "Evaluates gait and balance to determine fall risk",
            procedure: "Two-part assessment: balance (9 items) and gait (7 items) with specific maneuvers",
            scoring: "Total score 0-28; ≤18 indicates high fall risk; 19-23 indicates moderate risk",
            strengths: "Comprehensive, evaluates both static and dynamic balance, identifies specific deficits",
            limitations: "Takes 10-15 minutes to administer, requires training, some subjectivity in scoring"
          },
          {
            name: "Five Times Sit-to-Stand Test (FTSTS)",
            purpose: "Assesses lower extremity strength and transitional movements",
            procedure: "Time how long it takes to stand up and sit down five times as quickly as possible",
            scoring: ">12 seconds indicates increased fall risk in community-dwelling older adults",
            strengths: "Quick, requires only a chair, good measure of functional lower extremity strength",
            limitations: "Limited to lower body strength assessment, may be difficult for those with severe arthritis"
          },
          {
            name: "Morse Fall Scale",
            purpose: "Assesses fall risk in acute care and long-term care settings",
            procedure: "Six-item assessment including history of falling, secondary diagnoses, ambulatory aids, IV/heparin lock, gait, and mental status",
            scoring: "Total score 0-125; 0-24: low risk; 25-44: moderate risk; ≥45: high risk",
            strengths: "Quick, widely used in hospitals, easy to integrate into nursing assessment",
            limitations: "Designed for acute care, less applicable to community settings"
          },
          {
            name: "Functional Reach Test",
            purpose: "Measures stability during reaching tasks",
            procedure: "Measure the maximum distance a person can reach forward while standing in a fixed position",
            scoring: "<6 inches indicates limited functional balance and increased fall risk",
            strengths: "Quick, requires minimal equipment, correlates with center of pressure excursion",
            limitations: "Single-task assessment, doesn't evaluate all aspects of balance"
          },
          {
            name: "STRATIFY (St. Thomas Risk Assessment Tool in Falling Elderly Inpatients)",
            purpose: "Identify fall risk in hospitalized elderly patients",
            procedure: "Five-item assessment including prior falls, agitation, visual impairment, frequent toileting, and transfer/mobility score",
            scoring: "Score ≥2 indicates high fall risk",
            strengths: "Quick, specifically designed for hospital settings, good predictive value",
            limitations: "Less applicable to community settings, limited sensitivity in some populations"
          },
          {
            name: "4-Stage Balance Test",
            purpose: "Assess static balance in progressively challenging positions",
            procedure: "Patient holds four positions (feet side-by-side, semi-tandem, tandem, and one-foot stand) for 10 seconds each",
            scoring: "Inability to hold tandem stance for 10 seconds indicates increased fall risk",
            strengths: "Quick, no equipment needed, part of CDC STEADI toolkit",
            limitations: "Focuses only on static balance, limited sensitivity"
          }
        ]
      },
      populationSpecific: {
        title: "Population-Specific Considerations",
        populations: [
          {
            population: "Older Adults (65+)",
            recommendedTests: ["TUG", "Berg Balance Scale", "POMA"],
            specialConsiderations: "Consider fatigue, multiple comorbidities, and potential fear of falling"
          },
          {
            population: "Acute Care/Hospital Patients",
            recommendedTests: ["Morse Fall Scale", "STRATIFY", "Hendrich II Fall Risk Model"],
            specialConsiderations: "Assess impact of acute illness, medications, unfamiliar environment, and medical devices"
          },
          {
            population: "Neurological Conditions",
            recommendedTests: ["Berg Balance Scale", "Dynamic Gait Index", "Functional Gait Assessment"],
            specialConsiderations: "Adapt tests based on specific neurological deficits; consider cognitive components"
          },
          {
            population: "Cognitive Impairment",
            recommendedTests: ["TUG", "Performance-Oriented Assessment of Mobility", "Simplified tests with clear instructions"],
            specialConsiderations: "Use simple commands, demonstrate tasks, allow more time, assess behavioral factors"
          },
          {
            population: "Post-Surgical",
            recommendedTests: ["TUG", "FTSTS", "Functional Reach (when appropriate)"],
            specialConsiderations: "Consider pain, weight-bearing restrictions, and impact of anesthesia/medications"
          }
        ]
      },
      implementationGuide: {
        title: "Implementation Guide",
        steps: [
          {
            step: "Select Appropriate Tests",
            details: "Choose tests based on patient population, setting, available time/resources, and specific concerns"
          },
          {
            step: "Standardize Administration",
            details: "Follow test protocols exactly; use consistent instructions, equipment, and environment"
          },
          {
            step: "Document Thoroughly",
            details: "Record exact scores, observations, and environmental/situational factors that may have influenced performance"
          },
          {
            step: "Interpret Results Contextually",
            details: "Consider scores in relation to age norms, diagnosis, and functional requirements"
          },
          {
            step: "Develop Targeted Interventions",
            details: "Address specific deficits identified in the assessment (e.g., strength, balance, gait)"
          },
          {
            step: "Establish Reassessment Schedule",
            details: "Plan regular follow-up assessments to monitor progress and adjust interventions"
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of standardized assessment tests",
        questions: [
          {
            question: "Which test is most appropriate for quickly assessing fall risk in a busy clinical setting?",
            options: ["Berg Balance Scale", "Timed Up and Go (TUG)", "Tinetti POMA", "Manual Muscle Testing"],
            correctAnswer: "Timed Up and Go (TUG)",
            explanation: "The TUG test takes only 1-2 minutes to administer, requires minimal equipment, and provides a quick but effective screening for mobility and fall risk."
          },
          {
            question: "What score on the Berg Balance Scale indicates high fall risk?",
            options: ["Below 56", "Below 45", "Below 36", "Below 20"],
            correctAnswer: "Below 36",
            explanation: "A Berg Balance Scale score below 36 indicates high fall risk with nearly 100% probability of falls, while scores below 45 indicate increased risk."
          },
          {
            question: "Which assessment tool was specifically designed for hospital settings?",
            options: ["Berg Balance Scale", "Functional Reach Test", "STRATIFY", "Tinetti POMA"],
            correctAnswer: "STRATIFY",
            explanation: "The STRATIFY (St. Thomas Risk Assessment Tool in Falling Elderly Inpatients) was specifically developed to identify fall risk in hospitalized elderly patients."
          },
          {
            question: "What is the primary limitation of the Five Times Sit-to-Stand Test?",
            options: ["Takes too long to administer", "Requires expensive equipment", "Limited to lower body strength assessment", "Poor validity"],
            correctAnswer: "Limited to lower body strength assessment",
            explanation: "While valuable, the FTSTS primarily assesses lower extremity strength and doesn't evaluate other important fall risk factors like balance, gait, or environmental factors."
          }
        ]
      }
    }
  }
];

export const module3_lessons = [
  { isHeader: true, title: 'Caregiver Best Practices and Mobility Assistance' }, 
  {
    id: 'cbp-1.1', 
    title: 'Safe Techniques for Transfers and Ambulation', 
    duration: 20, 
    type: 'Video Lesson', 
    description: "Learn proper techniques for safely transferring patients between surfaces and assisting with walking.",
    content: {
      introduction: "Safe transfer and ambulation techniques are essential skills for caregivers. Proper technique protects both the caregiver from injury and the care recipient from falls, while promoting dignity and independence.",
      keyStats: [
        "Up to 30% of falls occur during transfers between surfaces",
        "Proper transfer techniques can reduce caregiver back injuries by up to 40%",
        "Assisted ambulation with proper technique reduces fall risk by 60%",
        "Over 75% of caregivers report using improper body mechanics at least occasionally"
      ],
      videoId: "safe_transfers",
      videoCaption: "This video demonstrates safe techniques for transferring patients between surfaces and assisting with ambulation to prevent falls.",
      transferTechniques: {
        title: "Safe Transfer Techniques",
        techniques: [
          {
            name: "Sit-to-Stand Transfer",
            steps: [
              "Position the person at the edge of the surface (bed, chair)",
              "Ensure feet are flat on floor and slightly apart",
              "Place your feet on either side of the person's feet",
              "Bend your knees and keep your back straight",
              "Have the person lean forward and push up with their hands",
              "Use a gait belt if needed for additional security",
              "Pivot together toward the destination surface"
            ],
            safetyTips: [
              "Never pull on the person's arms or under their shoulders",
              "Ensure the destination surface is stable and at appropriate height",
              "Lock wheels on wheelchairs, beds, or other movable surfaces",
              "Communicate clearly about each step before and during the transfer"
            ]
          },
          {
            name: "Bed-to-Wheelchair Transfer",
            steps: [
              "Position wheelchair at 45° angle to bed with wheels locked",
              "Remove or swing away footrests",
              "Assist person to sitting position at edge of bed",
              "Apply transfer belt securely around waist if needed",
              "Ensure feet are flat on floor",
              "Stand in front with your feet in a stride position",
              "Bend knees and keep back straight",
              "On count of three, assist person to stand and pivot toward chair",
              "Lower person gently into wheelchair"
            ],
            safetyTips: [
              "Ensure brakes are locked on both bed and wheelchair",
              "Position wheelchair on person's stronger side when possible",
              "Keep your center of gravity low during the transfer",
              "Use mechanical lift for dependent transfers when appropriate"
            ]
          },
          {
            name: "Toilet Transfers",
            steps: [
              "Ensure bathroom door opens fully and space allows for maneuvering",
              "Have clothing adjusted before beginning transfer",
              "Position wheelchair at 90° angle to toilet when possible",
              "Use grab bars or toilet safety frame for support",
              "Follow sit-to-stand transfer technique",
              "Ensure stability before releasing support"
            ],
            safetyTips: [
              "Use elevated toilet seats for easier transfers",
              "Install grab bars at appropriate heights and positions",
              "Ensure adequate lighting in bathroom",
              "Consider commode chair if bathroom access is difficult"
            ]
          }
        ]
      },
      ambulationAssistance: {
        title: "Safe Ambulation Assistance",
        techniques: [
          {
            name: "One-Person Assist",
            steps: [
              "Position yourself on the person's weaker side",
              "Apply gait belt securely around waist",
              "Hold gait belt from underneath with one hand",
              "Place your other hand on person's shoulder or upper arm",
              "Walk slightly behind and to the side",
              "Match your pace to the person's natural rhythm",
              "Provide verbal cues for obstacles or direction changes"
            ],
            safetyTips: [
              "Ensure proper footwear for both caregiver and care recipient",
              "Remove environmental hazards along walking path",
              "Be prepared to support weight if balance is lost",
              "Take rest breaks as needed"
            ]
          },
          {
            name: "Assisting with Walkers",
            steps: [
              "Ensure walker is at proper height (handles at wrist level when arms hang down)",
              "Position yourself slightly behind and to one side",
              "Instruct on proper walker technique: move walker forward, then step with weaker leg, then stronger leg",
              "Use gait belt for additional security if needed",
              "Monitor for proper posture (upright, not leaning forward)"
            ],
            safetyTips: [
              "Check walker for stability and proper maintenance",
              "Ensure rubber tips are intact and not worn",
              "Avoid rushing or distractions during ambulation",
              "Be aware of environmental challenges (thresholds, carpet edges)"
            ]
          },
          {
            name: "Stair Assistance",
            steps: [
              "Position yourself one step below when ascending (for forward support)",
              "Position yourself one step above when descending (to prevent forward falls)",
              "Use handrails when available",
              "Follow 'up with good, down with bad' rule for leg sequence",
              "Take one step at a time, ensuring stability before proceeding"
            ],
            safetyTips: [
              "Ensure adequate lighting on stairs",
              "Remove clutter and obstacles from stairways",
              "Consider stair lifts for those with significant mobility limitations",
              "Avoid carrying items while assisting on stairs"
            ]
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of safe transfer and ambulation techniques",
        questions: [
          {
            question: "When assisting a person to stand from a chair, where should you position your feet?",
            options: ["Directly in front of the person's feet", "On either side of the person's feet", "Behind the person's feet", "At least 2 feet away from the person"],
            correctAnswer: "On either side of the person's feet",
            explanation: "Positioning your feet on either side of the person's feet provides stability and a secure base of support during the transfer."
          },
          {
            question: "When assisting someone on stairs, where should you position yourself when going down the stairs?",
            options: ["One step above", "One step below", "On the same step", "Two steps below"],
            correctAnswer: "One step below",
            explanation: "When descending stairs, position yourself one step below to prevent forward falls and provide support."
          },
          {
            question: "Which is NOT a safe practice when using a gait belt?",
            options: ["Holding the belt from underneath", "Pulling upward on the belt during transfers", "Ensuring the belt is snug but not tight", "Positioning the belt around the waist"],
            correctAnswer: "Pulling upward on the belt during transfers",
            explanation: "Pulling upward on a gait belt can cause discomfort and potential injury. The belt should be used to guide and stabilize, not to lift the person's weight."
          }
        ]
      }
    }
  },
  {
    id: 'cbp-1.2', 
    title: 'Supporting Independence While Ensuring Safety', 
    duration: 15, 
    type: 'Interactive Lesson', 
    description: "Learn how to balance promoting independence with fall prevention.",
    content: {
      introduction: "Finding the balance between promoting independence and ensuring safety is a key challenge for caregivers. This module explores strategies to empower care recipients while minimizing fall risks.",
      keyStats: [
        "Excessive assistance can lead to a 30% decline in functional abilities within 2 weeks",
        "Promoting safe independence reduces depression by up to 40% in older adults",
        "Balanced approach to assistance reduces falls by 25% compared to either over-assistance or under-assistance",
        "90% of older adults rank maintaining independence as their top priority"
      ],
      videoId: "supporting_independence",
      videoCaption: "This video demonstrates strategies for balancing independence and safety when caring for individuals at risk of falls.",
      balancingStrategies: {
        title: "Strategies for Balancing Independence and Safety",
        strategies: [
          {
            strategy: "Graduated Assistance",
            description: "Provide only the minimum level of help needed for safe task completion",
            examples: [
              "Standby assistance - remaining nearby without physical contact",
              "Contact guard - light touch for reassurance without supporting weight",
              "Minimal assistance - helping with 25% or less of the task",
              "Moderate assistance - helping with about 50% of the task",
              "Maximum assistance - helping with 75% or more but not all of the task"
            ]
          },
          {
            strategy: "Environmental Adaptation",
            description: "Modify the environment to enable safe independence",
            examples: [
              "Rearranging furniture to create clear pathways",
              "Installing grab bars and handrails at key locations",
              "Using adaptive equipment like reachers or sock aids",
              "Improving lighting in activity areas",
              "Organizing frequently used items within easy reach"
            ]
          },
          {
            strategy: "Task Adaptation",
            description: "Modify how tasks are performed to accommodate limitations",
            examples: [
              "Breaking complex tasks into manageable steps",
              "Allowing extra time for task completion",
              "Incorporating rest periods during activities",
              "Simplifying tasks when possible",
              "Teaching energy conservation techniques"
            ]
          },
          {
            strategy: "Collaborative Goal Setting",
            description: "Work together to establish realistic safety and independence goals",
            examples: [
              "Identifying activities most important to the individual",
              "Setting specific, measurable, achievable goals",
              "Regularly reviewing and adjusting goals based on progress",
              "Celebrating successes and independence milestones",
              "Involving the person in decisions about assistance levels"
            ]
          }
        ]
      },
      riskAssessment: {
        title: "Assessing Risk vs. Independence",
        factors: [
          {
            factor: "Person Factors",
            considerations: [
              "Cognitive status and awareness of limitations",
              "Physical abilities and limitations",
              "History of falls or near-falls",
              "Motivation and emotional state",
              "Medication effects on balance and cognition"
            ]
          },
          {
            factor: "Activity Factors",
            considerations: [
              "Complexity and physical demands of the task",
              "Consequences of potential falls during the activity",
              "Frequency and necessity of the activity",
              "Available adaptations or assistive devices",
              "Time of day and fatigue levels"
            ]
          },
          {
            factor: "Environmental Factors",
            considerations: [
              "Lighting and visibility conditions",
              "Floor surfaces and potential hazards",
              "Space constraints and maneuverability",
              "Weather conditions (for outdoor activities)",
              "Presence of supportive features (handrails, grab bars)"
            ]
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of balancing independence and safety",
        questions: [
          {
            question: "Which approach best supports both independence and safety?",
            options: ["Doing all tasks for the person to prevent falls", "Allowing the person to do everything without assistance", "Providing graduated levels of assistance based on needs", "Focusing only on essential activities"],
            correctAnswer: "Providing graduated levels of assistance based on needs",
            explanation: "Graduated assistance allows the person to maintain independence by doing what they can safely while receiving appropriate support for challenging aspects."
          },
          {
            question: "What is 'contact guard' assistance?",
            options: ["Physically supporting most of a person's weight", "Standing nearby without touching", "Light touch for reassurance without supporting weight", "Using a gait belt for transfers"],
            correctAnswer: "Light touch for reassurance without supporting weight",
            explanation: "Contact guard involves maintaining light physical contact to provide reassurance and monitor balance without actually supporting the person's weight."
          },
          {
            question: "Which factor is LEAST important when assessing risk vs. independence?",
            options: ["Cognitive status", "Physical abilities", "The person's age", "Environmental hazards"],
            correctAnswer: "The person's age",
            explanation: "While age may correlate with certain risks, it's less important than individual factors like cognitive status, physical abilities, and environmental conditions when assessing risk vs. independence."
          }
        ]
      }
    }
  },
  {
    id: 'cbp-1.3', 
    title: 'Proper Body Mechanics for Caregivers', 
    duration: 15, 
    type: 'Video Lesson', 
    description: "Learn how to protect yourself from injury while providing care.",
    content: {
      introduction: "Proper body mechanics are essential for caregiver safety and longevity. Using correct techniques protects you from injury while ensuring effective care for your patients or loved ones.",
      keyStats: [
        "Over 52% of caregivers report back injuries related to patient care activities",
        "Proper body mechanics can reduce caregiver injuries by up to 65%",
        "The average cost of a back injury is $40,000 in medical care and lost work time",
        "Caregivers with proper body mechanics training have 40% fewer sick days"
      ],
      videoId: "body_mechanics",
      videoCaption: "This video demonstrates proper body mechanics techniques for caregivers to prevent injuries while providing care.",
      corePrinciples: {
        title: "Core Principles of Body Mechanics",
        principles: [
          {
            principle: "Maintain Neutral Spine",
            description: "Keep your back in its natural curve, avoiding excessive bending or twisting",
            applications: [
              "Use leg muscles instead of back muscles when lifting",
              "Avoid twisting while bearing weight",
              "Maintain the natural curves of your spine during all activities",
              "Keep your head aligned with your spine"
            ]
          },
          {
            principle: "Wide Base of Support",
            description: "Position your feet shoulder-width apart for stability",
            applications: [
              "Stagger your stance with one foot slightly forward when appropriate",
              "Keep feet flat on the floor during transfers",
              "Position yourself close to the person or object being moved",
              "Adjust foot position as needed during movement"
            ]
          },
          {
            principle: "Center of Gravity",
            description: "Keep loads close to your body and maintain your balance",
            applications: [
              "Hold objects close to your body when carrying",
              "Avoid reaching far forward or to the sides when lifting",
              "Lower your center of gravity by bending knees when lifting",
              "Maintain balance by keeping weight centered between your feet"
            ]
          },
          {
            principle: "Leverage and Mechanical Advantage",
            description: "Use your strongest muscle groups and appropriate leverage",
            applications: [
              "Use leg and hip muscles rather than back muscles",
              "Push rather than pull when possible",
              "Use assistive devices when available",
              "Slide objects instead of lifting when appropriate"
            ]
          }
        ]
      },
      commonActivities: {
        title: "Body Mechanics for Common Caregiving Activities",
        activities: [
          {
            activity: "Lifting Objects",
            technique: [
              "Stand close to the object with feet shoulder-width apart",
              "Bend at the knees and hips, not the waist",
              "Tighten abdominal muscles to support your back",
              "Lift with leg muscles while maintaining neutral spine",
              "Avoid twisting - pivot with your feet instead"
            ]
          },
          {
            activity: "Repositioning in Bed",
            technique: [
              "Raise bed to appropriate working height",
              "Lower side rail and stand with wide base of support",
              "Place one hand under shoulders, one under hips",
              "Ask person to bend knees and push with feet if able",
              "Use draw sheet for dependent repositioning",
              "Work with a partner for heavier individuals"
            ]
          },
          {
            activity: "Prolonged Standing Activities",
            technique: [
              "Maintain neutral spine alignment",
              "Keep one foot slightly elevated on a small stool",
              "Shift weight periodically to reduce strain",
              "Position work at appropriate height (elbow height)",
              "Take mini-breaks to stretch"
            ]
          },
          {
            activity: "Reaching and Overhead Work",
            technique: [
              "Use a stable step stool instead of reaching up",
              "Keep work directly in front of you",
              "Avoid extended reaching above shoulder height",
              "Take frequent breaks to rest arms",
              "Use long-handled tools when appropriate"
            ]
          }
        ]
      },
      preventiveStrategies: {
        title: "Preventive Strategies for Caregiver Safety",
        strategies: [
          "Regular stretching and strengthening exercises for core and back",
          "Using mechanical lifts for dependent transfers",
          "Taking micro-breaks throughout the day",
          "Maintaining good posture during all activities",
          "Asking for help with difficult transfers or movements",
          "Using assistive devices appropriately",
          "Planning movements before executing them"
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of proper body mechanics",
        questions: [
          {
            question: "When lifting an object from the floor, you should:",
            options: ["Bend at the waist with straight legs", "Bend at the knees and hips with a straight back", "Twist slightly to get better leverage", "Reach as far as possible to grasp the object"],
            correctAnswer: "Bend at the knees and hips with a straight back",
            explanation: "Bending at the knees and hips while maintaining a straight back (neutral spine) allows you to use your strong leg muscles instead of straining your back."
          },
          {
            question: "Which principle of body mechanics involves keeping loads close to your body?",
            options: ["Neutral spine", "Wide base of support", "Center of gravity", "Mechanical advantage"],
            correctAnswer: "Center of gravity",
            explanation: "The center of gravity principle emphasizes keeping loads close to your body to maintain balance and reduce strain on your back muscles."
          },
          {
            question: "What is the recommended working height for most caregiving tasks?",
            options: ["Knee height", "Hip height", "Elbow height", "Shoulder height"],
            correctAnswer: "Elbow height",
            explanation: "Elbow height is the optimal working height for most tasks as it allows you to maintain neutral posture without straining your back, shoulders, or arms."
          }
        ]
      }
    }
  },
  {
    id: 'cbp-1.4', 
    title: 'Effective Communication to Reduce Fear of Falling', 
    duration: 15, 
    type: 'Interactive Lesson', 
    description: "Learn communication strategies to address fear of falling and promote confidence.",
    content: {
      introduction: "Fear of falling can be as debilitating as falls themselves, leading to activity restriction, deconditioning, and increased fall risk. Effective communication strategies can help address this fear and promote confidence in mobility.",
      keyStats: [
        "Up to 70% of older adults who have fallen develop fear of falling",
        "Fear of falling leads to activity restriction in 50% of those affected",
        "Activity restriction due to fear increases actual fall risk by 30%",
        "Effective communication can reduce fear of falling by up to 40%"
      ],
      videoId: "effective_communication",
      videoCaption: "This video demonstrates effective communication techniques to help reduce fear of falling and promote confidence in mobility.",
      communicationStrategies: {
        title: "Effective Communication Strategies",
        strategies: [
          {
            strategy: "Active Listening",
            techniques: [
              "Give full attention without interrupting",
              "Acknowledge concerns without dismissing them",
              "Use open-ended questions to explore specific fears",
              "Reflect back what you hear to confirm understanding",
              "Watch for non-verbal cues indicating anxiety"
            ]
          },
          {
            strategy: "Positive Framing",
            techniques: [
              "Focus on capabilities rather than limitations",
              "Highlight progress and achievements",
              "Use encouraging language ('You can' instead of 'Don't')",
              "Reframe setbacks as learning opportunities",
              "Emphasize the benefits of activity rather than risks of inactivity"
            ]
          },
          {
            strategy: "Clear Instructions",
            techniques: [
              "Break down movements into simple steps",
              "Use concise, direct language",
              "Demonstrate movements when appropriate",
              "Confirm understanding before proceeding",
              "Maintain a calm, confident tone"
            ]
          },
          {
            strategy: "Building Trust",
            techniques: [
              "Be consistent and reliable in your assistance",
              "Honor preferences and respect autonomy",
              "Acknowledge when you don't know something",
              "Follow through on commitments",
              "Respect privacy and dignity"
            ]
          }
        ]
      },
      addressingFear: {
        title: "Addressing Specific Fears",
        approaches: [
          {
            fear: "Fear of Falling Again",
            strategies: [
              "Acknowledge the legitimacy of the fear",
              "Discuss specific circumstances of previous falls",
              "Identify controllable factors that can be addressed",
              "Develop a clear plan for similar situations",
              "Practice safe movements in controlled environments"
            ]
          },
          {
            fear: "Fear of Dependence",
            strategies: [
              "Emphasize that accepting help preserves independence",
              "Distinguish between temporary and permanent assistance",
              "Involve the person in decisions about assistance levels",
              "Highlight aspects of life where independence is maintained",
              "Frame assistive devices as tools for independence"
            ]
          },
          {
            fear: "Fear of Embarrassment",
            strategies: [
              "Normalize the need for assistance",
              "Share stories of others who have overcome similar challenges",
              "Provide discreet assistance in public settings",
              "Rehearse challenging situations in private",
              "Acknowledge feelings without judgment"
            ]
          },
          {
            fear: "Fear of Pain or Injury",
            strategies: [
              "Explain how proper technique minimizes discomfort",
              "Start with brief, comfortable activities",
              "Acknowledge pain concerns and address them promptly",
              "Discuss pain management strategies when appropriate",
              "Emphasize how strengthening reduces pain risk"
            ]
          }
        ]
      },
      motivationalTechniques: {
        title: "Motivational Techniques",
        techniques: [
          {
            technique: "Goal Setting",
            description: "Collaborate on meaningful, achievable mobility goals",
            examples: [
              "Walking to the mailbox independently",
              "Standing up from a chair without assistance",
              "Attending a grandchild's school event",
              "Resuming a favorite hobby or activity"
            ]
          },
          {
            technique: "Celebrating Progress",
            description: "Acknowledge and celebrate achievements, no matter how small",
            examples: [
              "Keeping a progress journal",
              "Taking photos of milestone achievements",
              "Sharing successes with family members",
              "Creating visual progress charts"
            ]
          },
          {
            technique: "Graded Exposure",
            description: "Gradually increasing challenge in safe, controlled environments",
            examples: [
              "Practicing in private before public settings",
              "Starting with short distances and increasing gradually",
              "Beginning with maximum support and reducing over time",
              "Adding environmental challenges incrementally"
            ]
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of communication strategies",
        questions: [
          {
            question: "Which communication approach is most effective for someone with fear of falling?",
            options: ["Telling them not to worry about falling", "Doing activities for them to keep them safe", "Using positive framing and focusing on capabilities", "Limiting activities to prevent falls"],
            correctAnswer: "Using positive framing and focusing on capabilities",
            explanation: "Positive framing focuses on what the person can do safely rather than limitations, helping build confidence while acknowledging real concerns."
          },
          {
            question: "What is 'graded exposure' in addressing fear of falling?",
            options: ["Exposing someone to their fears all at once", "Gradually increasing challenge in safe environments", "Showing videos of others falling", "Discussing worst-case scenarios"],
            correctAnswer: "Gradually increasing challenge in safe environments",
            explanation: "Graded exposure involves incrementally increasing the difficulty of mobility tasks in controlled settings, allowing the person to build confidence progressively."
          },
          {
            question: "Which statement about fear of falling is TRUE?",
            options: ["It affects only those who have fallen previously", "It's a helpful response that prevents falls", "It often leads to activity restriction that increases fall risk", "It typically resolves on its own within a few weeks"],
            correctAnswer: "It often leads to activity restriction that increases fall risk",
            explanation: "Fear of falling frequently leads to activity restriction, which causes deconditioning, muscle weakness, and reduced balance—all factors that actually increase fall risk."
          }
        ]
      }
    }
  }
];

export const module4_lessons = [
  { isHeader: true, title: 'Emergency Response and Post-Fall Care' }, 
  // Lessons for emergency response...
  {
      id: 'er-1.1', 
      title: 'Immediate Response Protocol', 
      duration: 15, 
      type: 'Simulation', 
      description: "Learn proper emergency response procedures for falls.",
      content: {
        introduction: "Quick, informed action following a fall can greatly reduce the risk of further injury or complications. This module prepares you to respond effectively by following best-practice emergency protocols in real-time scenarios.",
        
        keyStats: [
          "Falls are the leading cause of injury-related emergency visits among older adults",
          "Only 37% of fall incidents are reported, delaying critical care",
          "Proper first response can reduce complications by up to 60%",
          "Responders trained in fall protocol are 4x more likely to provide appropriate care"
        ],
      
        videoId: "fall_response_protocol",
        videoCaption: "This video walks through a step-by-step emergency response to a fall, emphasizing calm, clarity, and care at each stage.",
      
        responseSteps: {
          title: "Emergency Response Steps",
          steps: [
            {
              step: "Ensure Scene Safety",
              details: [
                "Check for hazards such as water spills, loose rugs, or unstable furniture",
                "Do not rush to assist before verifying your own safety"
              ]
            },
            {
              step: "Assess Responsiveness",
              details: [
                "Gently approach the person and speak clearly",
                "Look for movement, eye contact, or verbal response",
                "If unresponsive, call for emergency help immediately"
              ]
            },
            {
              step: "Call for Help",
              details: [
                "Dial emergency services or activate your facility's protocol",
                "Provide location, symptoms, and known medical history if available"
              ]
            },
            {
              step: "Support Without Moving",
              details: [
                "Do not attempt to lift or reposition unless in immediate danger",
                "Provide a blanket or coat for warmth and reassurance",
                "Monitor for changes in consciousness or discomfort"
              ]
            },
            {
              step: "Document and Report",
              details: [
                "Record time of fall, observations, and actions taken",
                "Complete incident reports per institutional policies",
                "Follow up with care team or family as required"
              ]
            }
          ]
        },
      
        addressingChallenges: {
          title: "Addressing Common Challenges",
          challenges: [
            {
              challenge: "Witnessing a fall unexpectedly",
              strategies: [
                "Take a deep breath to stay calm",
                "Focus on performing one step at a time",
                "Rely on your training — it's there to guide you"
              ]
            },
            {
              challenge: "Person is distressed or disoriented",
              strategies: [
                "Speak in a calm, reassuring tone",
                "Explain what you’re doing and why",
                "Avoid overwhelming them with too many questions"
              ]
            },
            {
              challenge: "Lack of immediate medical assistance",
              strategies: [
                "Continue monitoring vitals and comfort",
                "Use first aid principles where applicable",
                "Stay with the individual until help arrives"
              ]
            }
          ]
        },
      
        motivationalTechniques: {
          title: "Promoting Confidence in Response",
          techniques: [
            {
              technique: "Scenario Practice",
              description: "Prepare by rehearsing response protocols regularly",
              examples: [
                "Run monthly mock drills",
                "Role-play fall incidents with peers",
                "Review emergency policies and flowcharts"
              ]
            },
            {
              technique: "Peer Debriefing",
              description: "Encourage peer feedback after real or simulated incidents",
              examples: [
                "What went well during your response?",
                "What could be improved?",
                "How did you feel, and how can we support each other better?"
              ]
            },
            {
              technique: "Self-Efficacy Building",
              description: "Reinforce the responder’s ability to make a difference",
              examples: [
                "Reflect on past successful interventions",
                "Highlight the impact of calm, clear communication",
                "Recognize improvement over time"
              ]
            }
          ]
        },
      
        knowledgeCheck: {
          title: "Knowledge Check",
          description: "Evaluate your understanding of emergency fall response protocols.",
          questions: [
            {
              question: "What is the FIRST step when responding to a fall?",
              options: [
                "Check for responsiveness",
                "Call emergency services",
                "Move the individual to a chair",
                "Ensure the environment is safe"
              ],
              correctAnswer: "Ensure the environment is safe",
              explanation: "You should always check for hazards before approaching or assisting to prevent additional harm."
            },
            {
              question: "When is it appropriate to move the individual after a fall?",
              options: [
                "As soon as they say they’re okay",
                "If they're in a public area",
                "Only if the environment poses an immediate threat",
                "If they insist on standing up"
              ],
              correctAnswer: "Only if the environment poses an immediate threat",
              explanation: "Moving someone without assessing injuries or risks can worsen the situation unless there's danger (e.g., fire or unsafe location)."
            },
            {
              question: "What role does documenting the incident play?",
              options: [
                "It helps avoid blame",
                "It's a legal requirement only in hospitals",
                "It ensures continuous care and communication",
                "It’s optional in most fall cases"
              ],
              correctAnswer: "It ensures continuous care and communication",
              explanation: "Documentation helps healthcare providers understand the situation and plan appropriate follow-up care."
            }
          ]
        }
      }
      
  },
  // ... other emergency response lessons
  { isHeader: true, title: 'Technology and Innovations in Fall Prevention' }, 
  // Lessons for technology...
  {
    id: "monitoring-1",
    title: "Monitoring Protocols",
    description: "Implement effective monitoring systems for fall prevention.",
    duration: 15,
    type: "Video Lesson",
    content: {
      introduction: "Effective monitoring is a critical component of fall prevention, enabling timely intervention and tailored support. By implementing structured protocols, caregivers and healthcare providers can identify risk factors early, track changes in mobility, and prevent future incidents.",
      keyStats: [
        "Falls account for over 3 million emergency visits annually among older adults",
        "Continuous monitoring reduces fall-related injuries by up to 35%",
        "Early identification of mobility changes can lower fall risk by 25%",
        "Environmental assessments help identify over 70% of common in-home hazards"
      ],
      videoId: "monitoring_protocols",
      videoCaption: "This video explains how to set up effective fall monitoring systems and utilize assessment tools to support safe mobility.",
      monitoringSystems: {
        title: "Core Components of Monitoring Protocols",
        components: [
          {
            component: "Routine Risk Assessments",
            practices: [
              "Conduct standardized fall risk screenings quarterly",
              "Use tools like the Timed Up and Go (TUG) test or Berg Balance Scale",
              "Document and review any changes in strength, balance, or gait",
              "Flag patients with recent medication changes or hospitalizations",
              "Include subjective concerns like fear of falling in risk assessments"
            ]
          },
          {
            component: "Environmental Surveillance",
            practices: [
              "Conduct regular walk-throughs of living spaces",
              "Check for loose rugs, poor lighting, and cluttered walkways",
              "Ensure mobility aids are correctly fitted and maintained",
              "Reassess environments after any reported fall or near miss",
              "Engage patients in identifying perceived hazards"
            ]
          },
          {
            component: "Technology-Enabled Monitoring",
            practices: [
              "Utilize motion sensors and fall detection devices when appropriate",
              "Incorporate wearable monitors to track movement and patterns",
              "Use camera-assisted supervision in high-risk settings (with consent)",
              "Leverage electronic health records for incident tracking and alerts",
              "Analyze data trends for early warning signs of decline"
            ]
          },
          {
            component: "Real-Time Reporting Systems",
            practices: [
              "Establish a clear protocol for documenting and reporting falls",
              "Encourage immediate verbal reporting of near misses",
              "Create interdisciplinary communication workflows for follow-up",
              "Use visual cue systems (e.g., door signs) for high-risk individuals",
              "Involve family members in monitoring when appropriate"
            ]
          }
        ]
      },
      individualizedPlans: {
        title: "Personalized Monitoring Plans",
        strategies: [
          {
            goal: "Tailor frequency and methods based on individual risk level",
            methods: [
              "Daily monitoring for high-risk individuals with recent falls",
              "Weekly check-ins for moderate-risk residents",
              "Encourage self-reporting logs or apps for low-risk individuals",
              "Reassess risk level after every fall, hospitalization, or major change"
            ]
          },
          {
            goal: "Incorporate behavioral cues into monitoring",
            methods: [
              "Watch for decreased activity or unusual fatigue",
              "Track withdrawal from normal routines or social interactions",
              "Note reluctance to use mobility aids or ask for help",
              "Be alert to changes in mood or cognition"
            ]
          }
        ]
      },
      motivationalTechniques: {
        title: "Supporting Engagement in Monitoring",
        techniques: [
          {
            technique: "Transparency and Consent",
            description: "Ensure individuals understand the purpose of monitoring and feel empowered, not surveilled",
            examples: [
              "Explain how fall prevention keeps them independent longer",
              "Offer choices in types of monitoring (tech-based or manual)",
              "Respect privacy and involve individuals in review of their own data"
            ]
          },
          {
            technique: "Positive Reinforcement",
            description: "Celebrate consistent engagement in monitoring protocols",
            examples: [
              "Share progress in mobility or fall-free days",
              "Recognize proactive reporting of near-misses",
              "Offer praise or small rewards for daily participation"
            ]
          }
        ]
      },
      knowledgeCheck: {
        title: "Knowledge Check",
        description: "Test your understanding of fall prevention monitoring systems",
        questions: [
          {
            question: "Which of the following is part of an effective monitoring protocol?",
            options: ["Waiting until a fall occurs to assess risk", "Using outdated mobility equipment", "Regular environmental walk-throughs", "Avoiding patient involvement"],
            correctAnswer: "Regular environmental walk-throughs",
            explanation: "Environmental surveillance identifies hazards early and is a proactive element of fall prevention."
          },
          {
            question: "What is the benefit of using wearable technology in fall monitoring?",
            options: ["It eliminates the need for caregivers", "It guarantees falls won’t happen", "It helps track movement patterns and detect risk early", "It replaces all in-person check-ins"],
            correctAnswer: "It helps track movement patterns and detect risk early",
            explanation: "Wearable technology provides continuous data that can highlight changes in mobility and predict fall risk."
          },
          {
            question: "Why is it important to personalize monitoring plans?",
            options: ["To reduce the workload of staff", "Because everyone has the same fall risk", "To address individual needs and changing conditions", "To avoid using assessment tools"],
            correctAnswer: "To address individual needs and changing conditions",
            explanation: "Tailoring plans ensures monitoring is effective, respectful, and appropriate to each person’s risk level and lifestyle."
          }
        ]
      }
    }
    
  },
  // ... other technology lessons
];

// --- End of Exported Lesson Data Definitions ---

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

// Function to generate random fall scenarios for the Video Evaluation 2 tool
const generateRandomScenario = () => {
  // Arrays of scenario components for randomization
  const patients = [
    { age: 72, gender: 'woman', condition: 'arthritis and limited mobility' },
    { age: 85, gender: 'man', condition: 'Parkinson\'s disease' },
    { age: 68, gender: 'woman', condition: 'early-stage dementia' },
    { age: 79, gender: 'man', condition: 'post-stroke recovery' },
    { age: 81, gender: 'woman', condition: 'osteoporosis and vision impairment' },
    { age: 76, gender: 'man', condition: 'diabetes and peripheral neuropathy' },
    { age: 83, gender: 'woman', condition: 'mild cognitive impairment' },
    { age: 70, gender: 'man', condition: 'COPD and general weakness' }
  ];
  
  const locations = [
    'attempting to get out of bed',
    'walking to the bathroom',
    'reaching for an item in the kitchen',
    'during a transfer from wheelchair to chair',
    'while getting dressed',
    'navigating a step in the hallway',
    'getting up from a seated position',
    'while walking with an assistive device'
  ];
  
  const statuses = [
    'conscious but disoriented',
    'alert but experiencing pain in the hip area',
    'conscious with minor visible bruising',
    'awake but confused about what happened',
    'responsive but anxious and agitated',
    'conscious with complaints of dizziness',
    'alert but unable to move without assistance',
    'conscious with potential wrist injury from breaking the fall'
  ];
  
  // Randomly select components
  const patient = patients[Math.floor(Math.random() * patients.length)];
  const location = locations[Math.floor(Math.random() * locations.length)];
  const status = statuses[Math.floor(Math.random() * statuses.length)];
  
  // Construct the scenario
  return `A ${patient.age}-year-old ${patient.gender} with ${patient.condition} has fallen while ${location}. The patient is ${status}. Demonstrate the proper emergency response protocol, including assessment and communication procedures.`;
};

function FallPreventionCourse() {
  const [completedSteps, setCompletedSteps] = useState([]);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [additionalCaseIncorrectAttempts, setAdditionalCaseIncorrectAttempts] = useState(0);
  const [firstCaseCompleted, setFirstCaseCompleted] = useState(false);
  const [activeCaseStudy, setActiveCaseStudy] = useState('case1');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showEvaluationNotification, setShowEvaluationNotification] = useState(false);
  const [correctlyAnsweredQuestions, setCorrectlyAnsweredQuestions] = useState(new Set());
  const [expandedStep, setExpandedStep] = useState(null);
  const [activeSection, setActiveSection] = useState('learning');
  const [expandedModule, setExpandedModule] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(getCompletedLessons());
  const navigate = useNavigate();
  const [currentLesson, setCurrentLesson] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [recordedVideo, setRecordedVideo] = useState(null); // For previewing recorded video URL
  const [stream, setStream] = useState(null); // For camera stream
  const [isRecording, setIsRecording] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [isAiGenerated, setIsAiGenerated] = useState(true); // Track if analysis is AI or mock
  const [scenarioText, setScenarioText] = useState(generateRandomScenario());
  const theme = useTheme();
  const [activeModule, setActiveModule] = useState(null);
  const { moduleProgress, setModuleProgress } = useModuleProgress();
  const [isModuleStarted, setIsModuleStarted] = useState(false);
  const videoRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const MAX_ATTEMPTS = 3;

  // --- Video Evaluation Handlers ---
  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        setAnalysisError("File size exceeds 15MB limit. Please upload a smaller video file.");
        e.target.value = null;
        return;
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
      setUploadedVideo(file);
      setRecordedVideo(null);
      setAnalysisComplete(false);
      setAnalysisResults(null);
      setAnalysisError(null);
    }
  };

  const handleCameraControlClick = async () => {
    if (isRecording) {
      // Stop recording
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      // The onstop handler will process the video
    } else if (stream) {
      // Start recording
      chunksRef.current = [];
      const options = { mimeType: 'video/webm; codecs=vp9' }; // Prefer WebM for better compatibility
      let mediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(stream, options);
      } catch (e) {
        console.warn('WebM with VP9 failed, trying default:', e);
        try {
           mediaRecorder = new MediaRecorder(stream); // Fallback to default
        } catch (e2) {
          console.error('MediaRecorder creation failed:', e2);
          setAnalysisError('Could not create MediaRecorder. Your browser might not support video recording.');
          return;
        }
      }
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorderRef.current?.mimeType || 'video/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const videoFile = new File([blob], 'recorded-video.webm', { type: mimeType });

        if (videoFile.size > 15 * 1024 * 1024) {
          setAnalysisError("Recorded video exceeds 15MB limit. Please record a shorter video.");
          setStream(null);
          return;
        }

        setRecordedVideo(URL.createObjectURL(blob));
        setUploadedVideo(videoFile); // Set the recorded file for analysis
        setAnalysisComplete(false);
        setAnalysisResults(null);
        setAnalysisError(null);
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      };
      try {
        mediaRecorder.start();
        setIsRecording(true);
      } catch (e) {
         console.error('MediaRecorder start failed:', e);
         setAnalysisError('Could not start video recording.');
      }
      
    } else {
      // Start camera
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
        setUploadedVideo(null);
        setRecordedVideo(null);
        setAnalysisComplete(false);
        setAnalysisResults(null);
        setAnalysisError(null);
      } catch (err) {
        console.error("Error accessing camera:", err);
        setAnalysisError("Could not access camera. Please ensure permissions are granted and reload the page.");
      }
    }
  };
  // --- End Video Evaluation Handlers ---

  const additionalFlashcards = [
    {
      question: "What medical conditions increase Mr. Miller's fall risk?",
      options: [
        "Vision problems and arthritis",
        "Diabetes and hypertension",
        "Heart disease and asthma",
        "Osteoporosis and dementia"
      ],
      correctAnswer: 1,
      explanation: "Mr. Miller's Type 2 Diabetes can affect balance and sensation, while hypertension can cause dizziness, both increasing fall risk.",
      relatedStep: 0
    },
    {
      question: "What immediate action should be taken when assisting Mr. Miller?",
      options: [
        "Call for emergency assistance",
        "Assess his pain level",
        "Check surgical site",
        "Review medication schedule"
      ],
      correctAnswer: 1,
      explanation: "Pain assessment is crucial as it can affect mobility and increase fall risk post-surgery.",
      relatedStep: 1
    },
    {
      question: "How should Mr. Miller's mobility be supported?",
      options: [
        "Encourage independent movement",
        "Use a gait belt and walker",
        "Provide wheelchair only",
        "Strict bed rest"
      ],
      correctAnswer: 1,
      explanation: "A gait belt and walker provide necessary support while promoting safe mobility during recovery.",
      relatedStep: 2
    },
    {
      question: "What environmental modification is most important for Mr. Miller?",
      options: [
        "Remove all furniture",
        "Install grab bars",
        "Clear walking paths",
        "Change floor material"
      ],
      correctAnswer: 2,
      explanation: "Clear paths are essential for safe walker use and reducing fall risks during recovery.",
      relatedStep: 3
    },
    {
      question: "How often should Mr. Miller's progress be monitored?",
      options: [
        "Daily",
        "Weekly",
        "Every 4 hours",
        "After each activity"
      ],
      correctAnswer: 3,
      explanation: "Post-surgical recovery requires frequent monitoring, especially after physical activities.",
      relatedStep: 4
    }
  ];

  const flashcards = [
    {
      question: "What are the key immediate actions to take after finding Mrs. Vance on the floor?",
      options: [
        "Move her to bed, check vital signs, call family",
        "Do not move her, call for assistance, check ABCs",
        "Help her stand up, give water, document incident",
        "Call emergency services, start CPR, notify supervisor"
      ],
      correctAnswer: 1,
      explanation: "It's crucial not to move a fallen patient until properly assessed. Call for assistance and check ABCs (Airway, Breathing, Circulation) first.",
      relatedStep: 0
    },
    {
      question: "Which assessment should be prioritized first for Mrs. Vance?",
      options: [
        "Detailed medication review",
        "Musculoskeletal examination",
        "Consciousness and vital signs",
        "Environmental hazard assessment"
      ],
      correctAnswer: 2,
      explanation: "Initial assessment must focus on consciousness and vital signs to ensure patient stability before proceeding with other examinations.",
      relatedStep: 1
    },
    {
      question: "What specific aspects need focus during Mrs. Vance's comprehensive evaluation?",
      options: [
        "Only cognitive function due to mild impairment",
        "Just blood pressure due to hypertension",
        "Cardiac, neurological, and musculoskeletal assessment",
        "Only check for visible injuries"
      ],
      correctAnswer: 2,
      explanation: "Given her conditions (hypertension, cognitive impairment, osteoarthritis), a thorough assessment of all these systems is crucial.",
      relatedStep: 2
    },
    {
      question: "Who needs to be notified about Mrs. Vance's fall?",
      options: [
        "Only the supervisor",
        "Family and supervisor only",
        "Physician, family, and OT/PT specialists",
        "Just the emergency services"
      ],
      correctAnswer: 2,
      explanation: "All relevant parties need to be notified to ensure proper care coordination, especially given her multiple conditions.",
      relatedStep: 3
    },
    {
      question: "How should Mrs. Vance's vital signs be monitored?",
      options: [
        "Once daily for a week",
        "Every 4 hours for 24 hours",
        "Hourly for 4 hours, then every 4 hours for 24 hours",
        "Only when symptoms appear"
      ],
      correctAnswer: 2,
      explanation: "Close monitoring is crucial in the first 4 hours, followed by regular checks for the next 24 hours, especially due to her hypertension.",
      relatedStep: 4
    }
  ];

  const [showAdditionalCaseStudy, setShowAdditionalCaseStudy] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);

  // Watch for error threshold
  React.useEffect(() => {
    if (incorrectAttempts >= MAX_ATTEMPTS && !showAdditionalCaseStudy) {
      setShowErrorNotification(true);
      setShowAdditionalCaseStudy(true);
      // Reset after showing notification
      setTimeout(() => setShowErrorNotification(false), 5000);
    }
  }, [incorrectAttempts]);

  const caseStudy = {
    title: "Fall Risk Assessment Case: Mrs. Eleanor Vance",
    patientInfo: {
      name: "Mrs. Eleanor Vance",
      age: 82,
      position: "Sitting on the edge of her bed attempting to get up",
      conditions: [
        "Osteoarthritis (knees and hips)",
        "Mild cognitive impairment",
        "Hypertension (managed with medication)",
        "Multiple medications including diuretics"
      ]
    },
    objectives: [
      "Demonstrate proper immediate response protocol",
      "Conduct comprehensive assessment considering multiple conditions",
      "Implement appropriate monitoring schedule",
      "Coordinate communication with healthcare team and family",
      "Document all interventions and assessments accurately"
    ]
  };

  const additionalCaseStudy = {
    title: "Fall Risk Assessment Case: Mr. David Miller",
    patientInfo: {
      name: "Mr. David Miller",
      age: 36,
      position: "Standing in his kitchen, preparing breakfast",
      conditions: [
        "Type 2 Diabetes (managed with oral medication)",
        "Mild Hypertension (managed with lifestyle changes)",
        "Generalized Anxiety Disorder (managed with therapy and PRN medication)",
        "History of mild lower back pain"
      ]
    },
    objectives: [
      "Demonstrate proper immediate response protocol",
      "Conduct comprehensive assessment considering multiple conditions",
      "Implement appropriate monitoring schedule",
      "Coordinate communication with healthcare team and family",
      "Document all interventions and assessments accurately"
    ]
  };

  const additionalProtocolSteps = [
    {
      id: 0,
      title: 'Medical Risk Assessment',
      description: 'Identify and evaluate fall risk factors related to medical conditions.',
      content: [
        'Evaluate impact of diabetes on balance and sensation',
        'Assess blood pressure and its effects on dizziness',
        'Review anxiety levels and their impact on stability'
      ]
    },
    {
      id: 1,
      title: 'Pain Management',
      description: 'Monitor and manage lower back pain to ensure safe mobility.',
      content: [
        'Assess back pain levels during daily activities',
        'Review pain management techniques',
        'Monitor for pain-related mobility limitations'
      ]
    },
    {
      id: 2,
      title: 'Mobility Support',
      description: 'Provide appropriate guidance for safe movement.',
      content: [
        'Teach proper body mechanics for daily activities',
        'Demonstrate safe kitchen navigation techniques',
        'Guide proper posture during standing activities'
      ]
    },
    {
      id: 3,
      title: 'Environment Safety',
      description: 'Ensure kitchen and living areas are safe for daily activities.',
      content: [
        'Organize kitchen for easy access to frequently used items',
        'Remove potential trip hazards',
        'Ensure adequate lighting in all areas'
      ]
    },
    {
      id: 4,
      title: 'Progress Monitoring',
      description: 'Regular assessment of condition management and safety practices.',
      content: [
        'Monitor blood sugar and blood pressure readings',
        'Track anxiety levels and their management',
        'Document effectiveness of back pain management'
      ]
    }
  ];

  const originalProtocolSteps = [
    {
      title: "Immediate Response",
      description: "Do not move patient, assess consciousness and ABCs (Airway, Breathing, Circulation), call for assistance"
    },
    {
      title: "Initial Assessment",
      description: "Check vital signs, assess pain levels, and evaluate for any obvious injuries"
    },
    {
      title: "Comprehensive Evaluation",
      description: "Conduct thorough cardiac, neurological, and musculoskeletal assessment, focusing on ankle and lower back pain"
    },
    {
      title: "Notifications",
      description: "Alert physician, notify family (wife), request OT/PT evaluation"
    },
    {
      title: "Monitoring Protocol",
      description: "Monitor vital signs every 4 hours for 24 hours, conduct regular neurological checks"
    },
    {
      title: "Pain Management",
      description: "Offer appropriate pain relief, monitor effectiveness, document pain levels"
    },
    {
      title: "Follow-up Plan",
      description: "Arrange x-ray of right ankle, implement treatment plan based on findings, explore contributing factors"
    }
  ];

  const protocolSteps = [
    {
      title: "Immediate Response & Safety Check",
      description: "Ensure the environment is safe and free from further hazards. Assess if the patient is conscious and responsive. Check for any immediate signs of severe injury, such as head trauma or fractures."
    },
    {
      title: "Physical Assessment & Injury Evaluation",
      description: "Evaluate for pain, swelling, or deformities, especially in the wrist. Conduct a basic neurological check for confusion or cognitive impairment. Determine if the patient can move or bear weight safely."
    },
    {
      title: "Decision on Movement & Assistance",
      description: "If no major injury, assist the patient into a seated position before standing. If a fracture or serious injury is suspected, keep the patient still and stabilize the affected limb. Apply ice to reduce swelling and arrange for medical transport if needed."
    },
    {
      title: "Communication & Documentation",
      description: "Notify emergency services or medical personnel with a clear report of the incident. Inform family or caregivers about the fall and next steps. Document the time, cause, injuries, and environmental factors for future fall prevention."
    },
    {
      title: "Post-Fall Monitoring & Prevention",
      description: "Ensure medical evaluation for potential fractures or delayed complications. Assess and adjust assistive devices, medication, or home safety measures. Implement strength and balance training to prevent future falls."
    }
  ];

  // Define submodules for each main module
  const introductionSubmodules = [
    {
      id: 'intro-1',
      title: "Introduction to Fall Prevention",
      description: "Understand the significance of fall prevention in healthcare.",
      icon: <InfoIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      duration: 30, // Example duration
      infographicType: "introduction",
      lessons: [
        // ... lessons for Introduction ...
        {
          id: "1.1",
          title: "Understanding Fall Risks (Video)",
          description: "Learn about common causes and risk factors associated with falls.",
          duration: 10,
          type: "Video Lesson",
          content: `Understanding Fall Risks in Healthcare

          1. Physical Factors:
          • Age-Related Changes
            - Decreased muscle strength
            - Reduced balance and coordination
            - Slower reaction times
            - Decreased bone density
          • Musculoskeletal Issues
            - Arthritis and joint pain
            - Foot problems
            - Postural disorders
            - Previous injuries

          2. Sensory Deficits:
          • Vision Impairment
            - Decreased visual acuity
            - Poor depth perception
            - Reduced contrast sensitivity
            - Adaptation to light changes
          • Proprioception Issues
            - Reduced position sense
            - Decreased spatial awareness
            - Joint position insensitivity
            - Impaired body alignment

          3. Medication Effects:
          • High-Risk Categories
            - Sedatives and hypnotics
            - Antidepressants
            - Antihypertensives
            - Diuretics
          • Risk Mechanisms
            - Orthostatic hypotension
            - Drowsiness
            - Impaired coordination
            - Altered mental status

          4. Previous Falls:
          • History of Falls
            - Strongest predictor of future falls
            - Indicates underlying risk factors
            - Creates fear of falling
            - Often leads to activity restriction
          • Fall Circumstances
            - Analyzing when falls occur
            - Understanding environmental triggers
            - Identifying behavioral patterns
            - Recognizing near-miss events

          5. Environmental Hazards:
          • Home Environment
            - Poor lighting
            - Cluttered walkways
            - Loose rugs
            - Lack of grab bars
          • Clinical Settings
            - Unfamiliar surroundings
            - Inappropriate bed heights
            - Inadequate footwear
            - Slippery surfaces`
        },
        {
          id: "1.2",
          title: "Impact of Falls (Interactive)",
          description: "Explore the physical, psychological, and financial impact of falls.",
          duration: 10,
          type: "Interactive",
          content: `Impact of Falls in Healthcare Settings

          1. Physical Consequences:
          • Immediate Injuries
            - Fractures (especially hip fractures which occur in 5-10% of falls)
            - Head injuries and traumatic brain injury (TBI affects 32% of fall victims over 70)
            - Lacerations requiring sutures (present in 12-15% of documented falls)
            - Soft tissue injuries including bruising and hematomas (occur in 30-50% of falls)
          • Long-term Effects
            - Reduced mobility (40% never regain pre-fall mobility levels)
            - Chronic pain (affects 25-35% of fall injury survivors)
            - Permanent disability (10-15% of falls result in lasting functional impairment)
            - Increased risk of future falls (2-3 times higher after initial fall)

          2. Psychological Impact:
          • Post-Fall Syndrome
            - Fear of falling (affects 40-73% of those who have fallen previously)
            - Anxiety about movement and daily activities (reported by 65% of fall survivors)
            - Loss of confidence in balance abilities (leads to self-imposed restrictions)
            - Development of catastrophic thinking patterns about mobility
          • Depression and Mental Health
            - Clinical depression rates increase by 35% after serious falls
            - Social withdrawal due to embarrassment or fear (affects 50% of fall survivors)
            - Increased feelings of helplessness and dependency
            - Cognitive decline acceleration in older adults after falls

          3. Healthcare System Impact:
          • Clinical Resource Utilization
            - Emergency department visits (2.8 million annually due to falls in older adults)
            - Hospitalization rates (800,000+ hospitalizations annually from fall injuries)
            - Rehabilitation services (average 3-6 weeks for hip fracture recovery)
            - Extended care facility admissions (22% of fall patients require facility care)
          • Financial Burden
            - Average hospital cost per fall: $30,000-$50,000
            - Annual U.S. healthcare costs: $50+ billion for non-fatal falls
            - Long-term care costs: $75,000-$100,000 annually for nursing home care
            - Indirect costs: $25+ billion in lost productivity and caregiver time

          4. Quality of Life and Social Impact:
          • Independence and Living Situation
            - 40% of nursing home admissions occur after a fall
            - 25% of fallers reduce their daily activities
            - 70% report some level of activity restriction after falling
            - 60% experience decreased participation in social activities
          • Caregiver Impact
            - Family members provide average of 20+ hours weekly care after serious falls
            - Caregiver stress and burnout rates increase by 45-60%
            - Financial strain from reduced work hours or leaving employment
            - Relationship dynamics fundamentally altered by new care responsibilities

          5. Mortality Risk:
          • Fatal Fall Statistics
            - Falls are the leading cause of injury-related death in adults over 65
            - 32,000+ older adults die annually from fall complications
            - Mortality risk increases 4-fold in the year following a hip fracture
            - Men have a 33% higher fall-related mortality rate than women`
    },
        {
          id: "1.3",
          title: "High-Risk Populations",
          description: "Identify populations at higher risk for falls.", 
          duration: 10,
          type: "Case Study",
          content: `High-Risk Populations for Falls

          1. Elderly Individuals (65+):
          • Risk Factors
            - Decreased muscle strength
            - Balance problems
            - Slower reaction times
            - Multiple chronic conditions
          • Special Considerations
            - Regular strength training
            - Balance exercises
            - Environmental modifications

          2. Post-Operative Patients:
          • Immediate Risks
            - Effects of anesthesia
            - Pain medication
            - Weakness from bed rest
          • Monitoring Requirements
            - Frequent vital signs
            - Assistance with mobility
            - Pain management

          3. Neurological Conditions:
          • Common Conditions
            - Parkinson's disease
            - Multiple sclerosis
            - Stroke
            - Dementia
          • Specific Needs
            - Specialized mobility aids
            - Cognitive support
            - Environmental adaptation

          4. Medication-Related Risks:
          • High-Risk Medications
            - Psychotropics
            - Antihypertensives
            - Sedatives
            - Multiple medication use
          • Management Strategies
            - Regular medication review
            - Timing of medications
            - Alternative treatments

          5. Vision Impairment:
          • Adaptations Needed
            - Enhanced lighting
            - Contrast marking
            - Clear pathways
            - Regular vision checks`
        }
      ]
    },
    // Add other submodules relevant to "Understanding Fall Risks" if they exist elsewhere
  ];

  const assessmentSubmodules = [
     {
      id: 'assessment-main',
      title: "Risk Assessment Fundamentals",
      description: "Core concepts and methods for assessing fall risk.",
      icon: <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      duration: 25, // Example duration
      lessons: [
        {
          id: "2.1",
          title: "Assessment Tools Overview",
          description: "Introduction to various fall risk assessment tools.",
          duration: 10,
          type: "Video Lesson",
          content: `Overview of Morse Fall Scale, STRATIFY, Berg Balance Scale, TUG...` // Content omitted
        },
        {
          id: "2.2",
          title: "Comprehensive Assessment",
          description: "Performing a detailed fall risk assessment.",
          duration: 15,
          type: "Case Study",
          content: `Case study walkthrough of assessing a patient...` // Content omitted
        },
        // Add lesson for 'Documentation Best Practices' if relevant here
      ]
    },
    // Add other submodules relevant to "Risk Assessment and Prevention Strategies"
  ];

  const preventionStrategySubmodules = [
     {
      id: 'prevention-1',
      title: "Environmental & Educational Strategies",
      description: "Implementing environmental modifications and patient education.",
      icon: <LocalHospitalIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      duration: 40, // Combined duration estimate
      lessons: [
        {
          id: "3.1",
          title: "Environmental Modifications",
          description: "Learn to create safer environments to prevent falls.",
          duration: 20,
          type: "Video Lesson",
          content: {
            introduction: "Environmental modifications are among the most effective interventions for reducing fall risk. Simple changes to a patient's surroundings can dramatically decrease the likelihood of falls.",
            keyStats: [
              "60% of falls occur in the home environment",
              "Bathroom modifications can reduce fall risk by up to 35%",
              "Proper lighting improvements decrease fall incidents by 20-30%",
              "95% of hip fractures result from falls, many preventable with environment changes"
            ],
            environmentalZones: {
              title: "Key Areas for Modification",
              zones: [
                {
                  name: "Bathroom",
                  modifications: [
                    "Install grab bars near toilet and shower",
                    "Use non-slip mats in shower/tub",
                    "Add raised toilet seats with armrests",
                    "Ensure adequate lighting",
                    "Consider shower chairs/benches"
                  ]
                },
                {
                  name: "Bedroom",
                  modifications: [
                    "Arrange furniture to create clear pathways",
                    "Install nightlights for visibility",
                    "Use beds at proper height (20-23 inches)",
                    "Place nightstands within easy reach",
                    "Secure all rugs with non-slip backing"
                  ]
                },
                {
                  name: "Living Areas",
                  modifications: [
                    "Remove clutter from walkways",
                    "Secure or remove throw rugs",
                    "Add color contrast at level changes",
                    "Install handrails along corridors",
                    "Arrange furniture to support stability"
                  ]
                }
              ]
            },
            implementationSteps: {
              title: "Implementation Process",
              steps: [
                "Conduct a thorough home safety assessment",
                "Prioritize high-risk areas (bathroom, stairs, bedroom)",
                "Begin with low-cost, high-impact modifications",
                "Involve the patient in the process when possible",
                "Follow up and reassess after implementation"
              ]
            },
            costConsiderations: {
              title: "Cost-Effective Solutions",
              insights: [
                "Many effective modifications cost under $100",
                "Insurance may cover some medically necessary modifications",
                "Community programs often provide assistance for seniors",
                "Prioritize safety-critical changes over aesthetic ones",
                "Consider DIY options for simple modifications"
              ]
            },
            knowledgeCheck: {
              title: "Knowledge Check",
              description: "Test your understanding of environmental modifications",
              questions: [
                {
                  question: "Which area of the home has the highest fall risk?",
                  options: ["Kitchen", "Bathroom", "Living room", "Bedroom"],
                  answer: "Bathroom",
                  explanation: "Bathrooms combine multiple risk factors: slippery surfaces, transfers on/off toilet and in/out of tub, limited space, and hard surfaces if falls occur."
                },
                {
                  question: "Which modification is most effective for stair safety?",
                  options: ["Carpeted stairs", "Handrails on both sides", "Reflective tape on edges", "Good lighting"],
                  answer: "Handrails on both sides",
                  explanation: "Sturdy handrails on both sides provide essential support during stair navigation and help prevent falls on stairs, which can be particularly dangerous."
                },
                {
                  question: "What percentage of falls occur in the home environment?",
                  options: ["30%", "45%", "60%", "75%"],
                  answer: "60%",
                  explanation: "Research indicates that approximately 60% of falls occur in the home environment, making home modifications a critical intervention."
                }
              ]
            }
          }
        },
        {
          id: "3.2",
          title: "Safe Transfer Techniques",
          description: "Learn proper techniques for safely moving patients.",
          duration: 25,
          type: "Interactive Lesson",
          content: {
            introduction: "Safe transfer techniques are essential skills for preventing falls during patient movement. Proper technique protects both patients and caregivers from injury while promoting patient independence and dignity.",
            keyPrinciples: [
              "Assess patient's cognitive status and physical capabilities before any transfer",
              "Explain the transfer process to the patient before beginning",
              "Use proper body mechanics to protect yourself from injury",
              "Maintain the patient's center of gravity close to your body",
              "Never rush a transfer - patient safety comes first"
            ],
            transferTypes: {
              title: "Essential Transfer Techniques",
              techniques: [
                {
                  name: "Bed to Chair Transfer",
                  steps: [
                    "Position chair at 45° angle to bed with armrest against bed",
                    "Apply wheelchair brakes and remove footrests",
                    "Assist patient to sitting position at edge of bed",
                    "Ensure patient's feet are flat on floor",
                    "Brace patient's knees with your knees",
                    "Guide patient to pivot and sit in chair"
                  ],
                  keyConsiderations: "Maintain proper body mechanics; keep your back straight and knees bent"
                },
                {
                  name: "Toilet Transfer",
                  steps: [
                    "Position patient directly in front of toilet",
                    "Ensure stable footing on non-slip surfaces",
                    "Have patient reach for grab bars if available",
                    "Guide patient to pivot and lower slowly",
                    "Remove/adjust clothing only after patient is stable",
                    "Reverse process for standing transfer"
                  ],
                  keyConsiderations: "Privacy and dignity are essential; ensure bathroom is prepared before transfer begins"
                },
                {
                  name: "Car Transfer",
                  steps: [
                    "Position wheelchair parallel to car seat",
                    "Open car door fully and move front seat back",
                    "Lock wheelchair brakes and remove/swing footrests",
                    "Guide patient to standing with support",
                    "Have patient pivot and sit on car seat",
                    "Assist with legs after torso is secure"
                  ],
                  keyConsiderations: "Weather conditions may affect transfer safety; adjust technique accordingly"
                }
              ]
            },
            assistiveDevices: {
              title: "Transfer Assistive Devices",
              devices: [
                {
                  name: "Transfer Belt (Gait Belt)",
                  uses: "Provides secure handholds for guiding patient movements",
                  benefits: "Reduces risk of skin tears and improves caregiver leverage"
                },
                {
                  name: "Transfer Board",
                  uses: "Creates smooth bridge between transfer surfaces",
                  benefits: "Reduces friction and lifting requirements"
                },
                {
                  name: "Mechanical Lift",
                  uses: "For dependent patients with minimal weight-bearing ability",
                  benefits: "Prevents caregiver injury during transfers of heavy patients"
                },
                {
                  name: "Pivot Disc",
                  uses: "Assists with standing pivot transfers",
                  benefits: "Reduces friction during pivoting movements"
                }
              ]
            },
            knowledgeCheck: {
              title: "Knowledge Check",
              description: "Test your understanding of safe transfer techniques",
              questions: [
                {
                  question: "What should you assess before attempting any patient transfer?",
                  options: ["Room temperature", "Patient's blood pressure", "Patient's cognitive and physical capabilities", "Time of day"],
                  answer: "Patient's cognitive and physical capabilities",
                  explanation: "Understanding the patient's abilities helps determine the appropriate transfer technique and level of assistance required."
                },
                {
                  question: "What is the proper angle for positioning a wheelchair next to a bed for transfer?",
                  options: ["Directly parallel", "45° angle", "90° angle", "180° angle"],
                  answer: "45° angle",
                  explanation: "A 45° angle positions the chair close to the bed while providing enough space for the patient to pivot safely."
                },
                {
                  question: "When should you explain the transfer process to the patient?",
                  options: ["During the transfer", "After the transfer", "Before beginning the transfer", "Only if they ask"],
                  answer: "Before beginning the transfer",
                  explanation: "Explaining the process beforehand reduces patient anxiety and encourages cooperation."
                }
              ]
            }
          }
        },
        {
          id: "3.3",
          title: "Patient Education",
          description: "Master techniques for educating patients about fall prevention.",
          duration: 20,
          type: "Role Play",
          content: `Patient Education Strategies...` // Content omitted
        },
      ]
    },
    // Add other submodules relevant to "Risk Assessment and Prevention Strategies" (Prevention Part)
  ];


  const caregiverBestPracticesSubmodules = [
    {
        id: 'caregiver-1',
        title: 'Safe Transfers and Mobility',
        description: 'Techniques for assisting with mobility and transfers safely.',
        icon: <TransferWithinAStationIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
        duration: 40, // Example duration
        lessons: [
            {
                id: 'cbp-1.1', title: 'Safe Transfer Techniques', duration: 15, type: 'Video Lesson', content: '...'
            },
            {
                id: 'cbp-1.2', title: 'Using Mobility Aids', duration: 15, type: 'Interactive', content: '...'
            },
            {
                id: 'cbp-1.3', title: 'Promoting Independence', duration: 10, type: 'Case Study', content: '...'
            }
        ]
    }
    // Add other relevant submodules if they exist
  ];

  const emergencyResponseSubmodules = [
    {
        id: 'emergency-1',
        title: 'Post-Fall Actions',
        description: 'Protocols for immediate response and post-fall care.',
        icon: <EmergencyShareIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
        duration: 35, // Example duration
        lessons: [
            {
                id: 'er-1.1', title: 'Immediate Response Protocol', duration: 15, type: 'Simulation', content: '...'
            },
            {
                id: 'er-1.2', title: 'Post-Fall Assessment', duration: 10, type: 'Video Lesson', content: '...'
            },
             {
                id: "er-1.3", // Reusing assessment-3 as it fits well here conceptually
                title: "Post-Fall Documentation & Follow-up",
                description: "Learn effective monitoring and follow-up strategies after a fall.",
                duration: 10, // Adjusted duration
          type: "Interactive",
                content: `Monitoring and Follow-up Protocols...` // Content omitted
            },
        ]
    }
    // Add other relevant submodules
  ];

  const monitoringAndTechnologySubmodules = [
    {
      id: 'monitoring-main',
      title: "Ongoing Monitoring & Innovations",
      description: "Techniques for continuous monitoring and leveraging technology.",
      icon: <VisibilityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      duration: 30, // Example duration
      lessons: [
        {
          id: "monitoring-1",
          title: "Monitoring Protocols",
          description: "Implement effective monitoring systems for fall prevention.",
          duration: 15,
          type: "Video Lesson",
          content: `Comprehensive Monitoring Protocols...` // Content omitted
        },
        {
          id: "tech-1.1", // New ID for technology lesson
          title: "Technology in Fall Prevention",
          description: "Explore wearables, smart home devices, and AI.",
          duration: 15,
          type: "Video Lesson", // Or appropriate type
          content: `Overview of fall detection devices, monitoring systems...` // Add relevant content
        },
      ]
    }
    // Add other relevant submodules
  ];

  const handleModuleChange = (moduleIndex) => {
    // Allow expanding modules 0-4 (total of 5 modules)
    if (moduleIndex >= 0 && moduleIndex <= 4) {
      setExpandedModule(expandedModule === moduleIndex ? -1 : moduleIndex);
    }
  };

  const handleStepClick = (stepId) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  // Reset expanded step when switching case studies
  React.useEffect(() => {
    setExpandedStep(null);
  }, [activeCaseStudy]);

  const handleLessonComplete = (lessonId) => {
    // This function is kept for compatibility but is no longer needed
    // as lesson completion is now handled by the lessonService
    // and the ModuleContentPage component
  };
  
  // Effect to update completedLessons when returning to this page
  useEffect(() => {
    // Update the completedLessons state from localStorage when the component mounts
    setCompletedLessons(getCompletedLessons());
  }, [activeSection]); // Re-run when activeSection changes (e.g., when returning to content tab)

  const handleStartLesson = (moduleId, lessonId) => { // Updated arguments
    setCurrentLesson(lessonId);
    // Use BOTH moduleId and lessonId for navigation
    navigate(`/module-content/${moduleId}/${lessonId}`); 
    scrollToTop();
  };

  // Define the remaining submodule arrays
  const interventionSubmodules = [
    {
      title: "Clinical Interventions",
      description: "Learn about clinical interventions to prevent falls.",
      lessons: [
        {
          id: "intervention-1",
          title: "Medication Management",
          description: "Learn about medication management strategies to reduce fall risks.",
          duration: 15,
          type: "Interactive Guide",
          content: `Medication Management for Fall Prevention

          1. High-Risk Medications
          • Psychotropics
            - Benzodiazepines
            - Antipsychotics
            - Sedative-hypnotics
            - Antidepressants
          • Cardiovascular Agents
            - Antihypertensives
            - Diuretics
            - Beta-blockers
            - Vasodilators

          2. Medication Review Process
          • Regular Schedule
            - Upon admission
            - After falls
            - With new prescriptions
            - Quarterly for long-term care
          • Key Considerations
            - Necessity of each medication
            - Potential alternatives
            - Dose reduction possibilities
            - Timing optimization`
        },
        {
          id: "intervention-2",
          title: "Exercise Programs",
          description: "Design and implement effective exercise programs for fall prevention.",
          duration: 20,
          type: "Procedure Guide",
          content: `Exercise Programs for Fall Prevention

          1. Core Components
          • Balance Training
            - Static balance exercises
            - Dynamic balance activities
            - Perturbation training
            - Proprioceptive exercises
          • Strength Building
            - Lower extremity focus
            - Core strengthening
            - Progressive resistance
            - Functional movements

          2. Program Design
          • Frequency and Duration
            - 2-3 times weekly minimum
            - 30-60 minute sessions
            - 12+ weeks for optimal results
            - Ongoing maintenance
          • Progression Principles
            - Begin with seated exercises if needed
            - Advance to standing with support
            - Progress to unsupported standing
            - Incorporate dynamic movements`
        }
      ]
    },
    {
      title: "Multifactorial Approaches",
      description: "Learn about comprehensive approaches to fall prevention.",
      lessons: [
        {
          id: "intervention-3",
          title: "Multifactorial Approaches",
          description: "Implement comprehensive fall prevention strategies.",
          duration: 20,
          type: "Case Study",
          content: `Multifactorial Approaches to Fall Prevention

          1. Comprehensive Assessment
          • Medical Evaluation
            - Chronic condition management
            - Medication review and adjustment
            - Vision and hearing assessment
            - Neurological examination
          • Functional Assessment
            - Gait and balance evaluation
            - Muscle strength testing
            - Transfer ability assessment
            - Activities of daily living review

          2. Personalized Interventions
          • Exercise Programs
            - Balance training
            - Strength building
            - Flexibility improvement
            - Endurance development
          • Environmental Modifications
            - Home safety assessment
            - Assistive device provision
            - Lighting improvements
            - Removal of hazards`
        }
      ]
    }
  ];

  const monitoringSubmodules = [
    {
      title: "Emergency Response",
      description: "Learn proper emergency response procedures for falls.",
      lessons: [
        {
          id: "monitoring-1",
          title: "Immediate Response Protocol",
          description: "Learn the proper steps to take immediately after a fall occurs.",
          duration: 15,
          type: "Procedure Guide",
          content: `Immediate Response Protocol for Falls

          1. Initial Assessment
          • Primary Survey
            - Check responsiveness
            - Assess airway, breathing, circulation
            - Control any obvious bleeding
            - Check for signs of head injury
          • Secondary Survey
            - Assess for fractures or dislocations
            - Check for soft tissue injuries
            - Evaluate neurological status
            - Monitor vital signs

          2. Decision Making
          • Move vs. Don't Move
            - Suspected spinal injury: Do not move
            - Minor injury, safe environment: Assist to comfortable position
            - Serious injury: Maintain position, call emergency services
            - No injury: Assist to rise using proper technique`
        },
        {
          id: "monitoring-2",
          title: "Post-Fall Assessment",
          description: "Conduct a thorough assessment after a fall has occurred.",
          duration: 15,
          type: "Interactive Guide",
          content: `Post-Fall Assessment Protocol

          1. Clinical Evaluation
          • Immediate Assessment
            - Vital signs monitoring
            - Pain assessment
            - Neurological checks
            - Injury documentation
          • Follow-up Evaluation
            - 24-hour monitoring schedule
            - Delayed symptom identification
            - Functional ability reassessment
            - Psychological impact evaluation

          2. Root Cause Analysis
          • Circumstantial Factors
            - Time and location
            - Activity during fall
            - Environmental conditions
            - Assistive devices used
          • Contributing Factors
            - Medication timing
            - Recent health changes
            - Staffing considerations
            - Equipment functionality`
        }
      ]
    },
    {
      title: "Monitoring Protocols",
      description: "Learn about ongoing monitoring techniques for fall prevention.",
      lessons: [
        {
          id: "monitoring-3",
          title: "Ongoing Monitoring",
          description: "Implement continuous monitoring protocols for high-risk patients.",
          duration: 15,
          type: "Procedure Guide",
          content: `Ongoing Monitoring Protocols

          1. Regular Assessment Schedule
          • Daily Checks
            - Mobility status
            - Medication effects
            - Mental status changes
            - Environmental checks
          • Weekly Reviews
            - Progress evaluation
            - Exercise compliance
            - Safety measure effectiveness
            - New risk factors

          2. Documentation Requirements
          • Progress Notes
            - Changes in condition
            - New symptoms
            - Intervention effectiveness
            - Patient compliance
          • Incident Reports
            - Near-miss events
            - Actual falls
            - Contributing factors
            - Outcomes`
        }
      ]
    }
  ];

  // Effect to handle error threshold
  React.useEffect(() => {
    if (incorrectAttempts >= MAX_ATTEMPTS && !showAdditionalCaseStudy) {
      setShowErrorNotification(true);
      setShowAdditionalCaseStudy(true);
      setTimeout(() => setShowErrorNotification(false), 5000);
    }
  }, [incorrectAttempts]);

  // Effect to handle case study completion
  React.useEffect(() => {
    if (firstCaseCompleted && showAdditionalCaseStudy) {
      setActiveCaseStudy('case2');
      setCompletedSteps([]);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000);
    }
  }, [firstCaseCompleted, showAdditionalCaseStudy]);

  const [shouldResetIndex, setShouldResetIndex] = useState(false);
  const [courseCompleted, setCourseCompleted] = useState(false);

  const handleCorrectAnswer = (cardIndex) => {
    const currentCards = activeCaseStudy === 'case1' ? flashcards : additionalFlashcards;
    const card = currentCards[cardIndex];
    
    // Create a prefixed index to separate case study 1 and 2 answers
    const prefixedIndex = `${activeCaseStudy}-${cardIndex}`;
    const newCorrectAnswers = new Set(correctlyAnsweredQuestions);
    newCorrectAnswers.add(prefixedIndex);
    setCorrectlyAnsweredQuestions(newCorrectAnswers);

    // Count correctly answered questions for the current case study
    const currentCaseAnswers = Array.from(newCorrectAnswers)
      .filter(id => id.startsWith(activeCaseStudy))
      .length;

    // Check completion for case studies
    if (activeCaseStudy === 'case1' && currentCaseAnswers === 5) {
      setFirstCaseCompleted(true);

      // Handle course completion based on whether case study 2 is needed
      if (showAdditionalCaseStudy) {
        // Switch to case study 2 if needed
        setActiveCaseStudy('case2');
        setShouldResetIndex(true);
        setShowSuccessNotification(true);
        setTimeout(() => {
          setShowSuccessNotification(false);
          setShouldResetIndex(false);
        }, 1500);
      } else {
        // Complete course if no case study 2 is needed
        setCourseCompleted(true);
        setShowSuccessNotification(true);
        setTimeout(() => {
          setShowSuccessNotification(false);
          setActiveSection('video');
        }, 2000);
      }
    } else if (activeCaseStudy === 'case2' && currentCaseAnswers === 5) {
      // Case study 2 completion
      setCourseCompleted(true);
      setShowSuccessNotification(true);
      setTimeout(() => {
        setShowSuccessNotification(false);
        setActiveSection('video');
      }, 2000);
    }

    // Update completed steps for protocol diagram
    if (card.relatedStep !== undefined) {
      const newCompletedSteps = [...completedSteps];
      if (!newCompletedSteps.includes(card.relatedStep)) {
        newCompletedSteps.push(card.relatedStep);
        setCompletedSteps(newCompletedSteps);
      }
    }
  };

  const handleIncorrectAnswer = () => {
    if (activeCaseStudy === 'case1') {
      setIncorrectAttempts(prev => Math.min(prev + 1, MAX_ATTEMPTS));
    } else {
      setAdditionalCaseIncorrectAttempts(prev => Math.min(prev + 1, MAX_ATTEMPTS));
    }
  };

  const ErrorCrosses = () => {
    const attempts = activeCaseStudy === 'case2' ? additionalCaseIncorrectAttempts : incorrectAttempts;
    
    return (
      <Box sx={{ 
        position: 'absolute', 
        top: -24,
        right: 24,
        display: 'flex', 
        gap: 1.5,
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: 'background.paper',
        boxShadow: 2,
        border: 1,
        borderColor: 'divider'
      }}>
        {[...Array(MAX_ATTEMPTS)].map((_, index) => (
          <CloseIcon
            key={index}
            sx={{
              color: index < attempts ? 'error.main' : 'text.disabled',
              fontSize: '1.75rem',
              filter: index < attempts ? 'drop-shadow(0 0 2px rgba(211, 47, 47, 0.5))' : 'none',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    );
  };

  // --- New Data Structure with Headers ---

  const module1_lessons = [
    { isHeader: true, title: 'Understanding Fall Risks' }, // Header for Card 1
    // --- Restructured Lesson 1.1 --- 
    {
      id: "1.1",
      // title: "Understanding Fall Risks (Video)", // Keeping original title for now
      title: "Common Causes of Falls", // Title from screenshot
      description: "Falls are rarely caused by a single factor; instead, they typically result from a complex interaction of multiple risk factors. Understanding these common causes is the first step in effective prevention.", // Description from screenshot
      duration: 10, // Adjust duration if needed
      type: "Detailed Lesson", // Changed type
      content: { // <<<< CHANGED TO OBJECT
        introduction: "Falls represent the leading cause of injury among older adults. Understanding the most common causes and risk factors is essential for implementing effective prevention strategies and ensuring patient safety.",
        keyStats: [
          "More than 1 in 4 older adults (65+) fall each year, with 36 million falls annually",
          "Every 11 seconds, an older adult is treated in the emergency room for a fall",
          "Falls result in over 3 million ER visits and 800,000 hospitalizations yearly",
          "The healthcare cost burden exceeds $50 billion annually"
        ],
        riskFactors: {
          intrinsic: [
            "Muscle weakness, particularly in lower extremities",
            "Balance and gait problems",
            "Visual impairments and poor depth perception",
            "Cognitive impairment and confusion",
            "Chronic conditions: arthritis, Parkinson's, dementia",
            "Medication effects, especially polypharmacy (4+ medications)"
          ],
          extrinsic: [
            "Poor lighting and high-glare surfaces",
            "Loose rugs and cluttered walkways",
            "Lack of grab bars and handrails",
            "Uneven surfaces and thresholds",
            "Improper footwear (ill-fitting, slippery soles)",
            "Unfamiliar environments (hospital, new living arrangements)"
          ]
        },
        assessmentFramework: {
            title: "Fall Risk Assessment Framework",
            description: "A comprehensive assessment should evaluate these key domains:",
            steps: [
              "Medical History: Review conditions, medications, and previous falls",
              "Physical Assessment: Evaluate gait, balance, and muscle strength",
              "Environmental Assessment: Identify hazards in the living environment",
              "Standardized Testing: Use validated tools like Morse Fall Scale or TUG test",
              "Regular Reassessment: Monitor for changes in condition or environment"
            ]
        },
        evidenceInsights: {
            title: "Evidence-Based Prevention Insights",
            description: "Research has revealed several important insights about fall prevention:",
            insights: [
              "Falls are rarely caused by a single factor; most involve multiple risk factors",
              "30-50% of falls among older adults are preventable through targeted interventions",
              "Multifactorial interventions addressing several risk factors show greatest effectiveness",
              "Regular strength and balance exercises can reduce fall risk by up to 35%",
              "The fear of falling often leads to reduced activity, paradoxically increasing fall risk"
            ]
        },
        highRiskPopulations: {
            title: "High-Risk Populations",
            groups: [
                {
                    name: "Cognitive Impairment",
                    points: [
                        "2-3 times higher fall risk with dementia",
                        "Impaired judgment may lead to unsafe behaviors",
                        "May not recall or report falls that occur",
                        "Require more frequent supervision",
                        "Benefit from simple, consistent routines"
                    ]
                },
                {
                    name: "Post-Surgical Patients",
                    points: [
                        "Anesthesia effects can persist 24+ hours",
                        "Pain medication may cause dizziness",
                        "Unfamiliar hospital environment increases risk",
                        "Pain may alter normal movement patterns",
                        "Require close monitoring during ambulation"
                    ]
                }
            ]
        },
        knowledgeCheck: {
          title: "Knowledge Check",
          description: "Test your understanding of fall risk factors",
          questions: [
            {
              question: "Which is NOT considered an intrinsic risk factor for falls?",
              options: ["Muscle weakness", "Poor lighting", "Previous falls", "Vision impairment"],
              answer: "Poor lighting",
              explanation: "Poor lighting is an extrinsic (environmental) factor, while the others are intrinsic (related to the individual's condition)."
            },
            {
              question: "Which approach has shown the greatest effectiveness for fall prevention?",
              options: ["Medication management only", "Environmental modifications only", "Multifactorial interventions", "Exercise programs only"],
              answer: "Multifactorial interventions",
              explanation: "Comprehensive approaches addressing multiple risk factors simultaneously show the greatest impact on reducing falls."
            },
            {
              question: "What percentage of falls are estimated to be preventable?",
              options: ["10-20%", "30-50%", "60-70%", "80-90%"],
              answer: "30-50%",
              explanation: "Current research suggests that 30-50% of falls can be prevented through appropriate interventions targeting modifiable risk factors."
            }
          ]
        }
        // NOTE: Visual elements like graphs/charts need separate components or data structures
      } // <<<< END OF CONTENT OBJECT
    },
    // --- End of Restructured Lesson 1.1 ---
    {
      id: "1.2",
      title: "Impact of Falls (Interactive)",
      description: "Explore the physical, psychological, and financial impact of falls.",
      duration: 10,
      type: "Interactive",
      // Keep string content for now, or structure it later
      content: `Impact of Falls in Healthcare Settings...` 
    },
    {
      id: "1.3", 
      title: "High-Risk Populations", 
      description: "Identify populations at higher risk for falls.", 
      duration: 10, 
      type: "Case Study", 
      // Keep string content for now, or structure it later
      content: `High-Risk Populations for Falls...` 
    },
    {
      id: "1.4", 
      title: "Physical, Environmental, and Medication-Related Risks", 
      description: "Identify populations at higher risk for falls.", 
      duration: 10, 
      type: "Case Study", 
      // Keep string content for now, or structure it later
      content: `High-Risk Populations for Falls...` 
    },
    {
      id: "1.5", 
      title: "Psychological and Emotional Impact of Falls", 
      description: "Understand how falls affect mental health, confidence, and quality of life, and learn strategies to address these impacts.", 
      duration: 10, 
      type: "Case Study", 
      // Keep string content for now, or structure it later
      content: `High-Risk Populations for Falls...` 
    }
  ];

  const module2_lessons = [
    { 
      id: "2.1",
      title: "Comprehensive Fall Risk Assessment",
      description: "Learn how to conduct a thorough assessment of fall risk factors.",
      duration: 25,
      type: "Interactive Lesson",
      content: `Standardized Fall Risk Assessment Tools...` // Keep string for now
    },
    {
      id: "2.2",
      title: "Medication Review",
      description: "Learn how to identify medications that increase fall risk.",
      duration: 20,
      type: "Interactive Lesson",
      content: {
        introduction: "Medication review is a critical component of fall risk assessment. Many commonly prescribed medications can increase the risk of falls through side effects such as dizziness, sedation, orthostatic hypotension, vision changes, or altered balance and coordination.",
        statistics: [
          "Patients taking four or more medications have a 2-3 times greater fall risk",
          "Psychotropic medications can increase fall risk by up to 47%",
          "Nearly 40% of falls among older adults involve medication as a contributing factor",
          "Medication review and adjustment can reduce fall rates by up to 39%"
        ],
        highRiskMedications: {
          title: "High-Risk Medication Categories",
          description: "The following medication types are commonly associated with increased fall risk:",
          categories: [
            {
              category: "Psychotropics",
              examples: "Antidepressants, antipsychotics, benzodiazepines, sedative-hypnotics, anxiolytics",
              mechanism: "Cause sedation, cognitive impairment, orthostatic hypotension, and balance problems",
              recommendations: "Consider dose reduction, alternative therapies, or gradual discontinuation when appropriate"
            },
            {
              category: "Cardiovascular Medications",
              examples: "Antihypertensives, diuretics, beta-blockers, ACE inhibitors, nitrates",
              mechanism: "May cause orthostatic hypotension, dehydration, electrolyte imbalances, or syncope",
              recommendations: "Monitor blood pressure in both lying and standing positions; adjust doses to minimize orthostatic changes"
            },
            {
              category: "Anticonvulsants",
              examples: "Phenytoin, carbamazepine, gabapentin, pregabalin",
              mechanism: "Can cause ataxia, dizziness, and impaired coordination",
              recommendations: "Monitor for side effects closely; consider alternative agents if balance issues develop"
            },
            {
              category: "Opioid Analgesics",
              examples: "Morphine, oxycodone, hydrocodone, tramadol",
              mechanism: "Cause sedation, cognitive impairment, and balance disturbances",
              recommendations: "Use lowest effective dose; consider non-opioid alternatives when possible"
            },
            {
              category: "Anticholinergics",
              examples: "First-generation antihistamines, bladder antimuscarinics, tricyclic antidepressants",
              mechanism: "Cause blurred vision, confusion, and cognitive impairment",
              recommendations: "Avoid in older adults when possible; use alternatives with lower anticholinergic burden"
            }
          ]
        },
        medicationReviewProcess: {
          title: "Structured Medication Review Process",
          steps: [
            {
              step: "Collect Comprehensive Medication List",
              details: "Document all prescription medications, over-the-counter products, supplements, and herbal remedies"
            },
            {
              step: "Identify High-Risk Medications",
              details: "Flag medications known to increase fall risk based on established criteria"
            },
            {
              step: "Evaluate Appropriateness",
              details: "Assess indication, effectiveness, dosage, and continued need for each medication"
            },
            {
              step: "Review Timing and Administration",
              details: "Consider medication timing, interactions, and administration challenges"
            },
            {
              step: "Develop Modification Plan",
              details: "Collaborate with prescribers on potential changes to reduce fall risk"
            },
            {
              step: "Monitor and Follow Up",
              details: "Track changes in symptoms, side effects, and fall risk after modifications"
            }
          ]
        },
        interventionStrategies: {
          title: "Medication Intervention Strategies",
          strategies: [
            "Deprescribing: Systematically withdrawing inappropriate medications",
            "Dose adjustment: Reducing doses to minimize side effects while maintaining benefits",
            "Therapeutic substitution: Switching to safer alternatives within the same class",
            "Timing adjustment: Changing administration times to minimize peak side effects",
            "Medication reconciliation: Eliminating duplicate therapies and unnecessary medications",
            "Non-pharmacological alternatives: Suggesting evidence-based non-drug approaches"
          ]
        },
        knowledgeCheck: {
          title: "Knowledge Check",
          description: "Test your understanding of medication review for fall prevention",
          questions: [
            {
              question: "Which medication class has the strongest association with increased fall risk?",
              options: ["Antibiotics", "Psychotropics", "Vitamins", "Thyroid medications"],
              answer: "Psychotropics",
              explanation: "Psychotropic medications (antidepressants, antipsychotics, benzodiazepines, etc.) significantly increase fall risk through multiple mechanisms including sedation and balance impairment."
            },
            {
              question: "How many medications typically constitute polypharmacy with increased fall risk?",
              options: ["2 or more", "4 or more", "10 or more", "Any number of medications"],
              answer: "4 or more",
              explanation: "Taking four or more medications (polypharmacy) is associated with a 2-3 times greater risk of falls."
            },
            {
              question: "Which is NOT a recommended step in the medication review process?",
              options: ["Collecting a comprehensive medication list", "Immediately stopping all high-risk medications", "Evaluating medication appropriateness", "Developing a modification plan"],
              answer: "Immediately stopping all high-risk medications",
              explanation: "Abruptly stopping medications can cause withdrawal effects or symptom recurrence. A careful, planned approach with prescriber collaboration is essential."
            }
          ]
        }
      }
    },
    {
      id: "2.3",
      title: "Safe Home and Facility Modifications",
      description: "Learn how to identify and implement environmental modifications to reduce fall risks in homes and care facilities.",
      duration: 20,
      type: "Interactive Lesson",
      content: {
        introduction: "Environmental hazards are a significant contributor to falls. Implementing targeted modifications to living spaces can dramatically reduce fall risk and create safer environments for vulnerable individuals.",
        keyStats: [
          "Environmental factors contribute to approximately 30-50% of falls",
          "Home safety modifications can reduce fall risk by up to 39%",
          "Simple modifications like improved lighting can reduce falls by 20%",
          "Over 70% of falls in healthcare facilities involve environmental factors"
        ],
        commonHazards: {
          title: "Common Environmental Hazards",
          categories: [
            {
              area: "Flooring and Walkways",
              hazards: [
                "Loose rugs and mats without non-slip backing",
                "Highly polished or wet floors",
                "Uneven surfaces and thresholds",
                "Cluttered walkways and corridors",
                "Exposed cords and cables"
              ]
            },
            {
              area: "Bathroom",
              hazards: [
                "Slippery surfaces in tubs and showers",
                "Lack of grab bars near toilets and bathing areas",
                "Low toilet seats",
                "Poor lighting",
                "Unsecured bath mats"
              ]
            },
            {
              area: "Bedroom",
              hazards: [
                "Inadequate lighting for nighttime navigation",
                "Beds at improper height",
                "Unstable furniture",
                "Cluttered pathways to bathroom",
                "Lack of nightlights"
              ]
            },
            {
              area: "Stairs and Steps",
              hazards: [
                "Lack of secure handrails",
                "Poor lighting",
                "Uneven steps",
                "Lack of color contrast at step edges",
                "Cluttered stairs"
              ]
            },
            {
              area: "Kitchen",
              hazards: [
                "Items stored out of reach (too high or too low)",
                "Slippery floors",
                "Poor lighting",
                "Unstable step stools",
                "Lack of seating for tasks"
              ]
            }
          ]
        },
        modificationStrategies: {
          title: "Evidence-Based Modification Strategies",
          strategies: [
            {
              area: "Flooring and Walkways",
              modifications: [
                "Remove or secure loose rugs with double-sided tape or non-slip backing",
                "Use non-slip floor treatments on slippery surfaces",
                "Repair uneven flooring and mark thresholds with contrasting colors",
                "Clear pathways of furniture and obstacles",
                "Secure cords along walls or under appropriate covers"
              ]
            },
            {
              area: "Bathroom",
              modifications: [
                "Install grab bars by toilets and in shower/tub areas (properly anchored to studs)",
                "Use non-slip mats in bathtubs and shower floors",
                "Install raised toilet seats for easier transfers",
                "Ensure adequate lighting with accessible switches",
                "Consider shower chairs and handheld shower heads"
              ]
            },
            {
              area: "Bedroom",
              modifications: [
                "Install nightlights or motion-sensor lighting",
                "Adjust bed height to allow feet to touch floor when sitting",
                "Ensure sturdy bedside tables with stable lamps",
                "Keep commonly needed items within reach",
                "Consider bed rails if appropriate (with safety assessment)"
              ]
            },
            {
              area: "Stairs and Steps",
              modifications: [
                "Install secure handrails on both sides of stairs",
                "Ensure adequate lighting with switches at top and bottom",
                "Mark step edges with contrasting tape or paint",
                "Repair loose or uneven steps",
                "Consider stair lifts for those unable to safely navigate stairs"
              ]
            },
            {
              area: "Kitchen",
              modifications: [
                "Reorganize frequently used items to waist-level shelves",
                "Use stable step stools with handrails when needed",
                "Install adequate task lighting",
                "Use non-slip mats in front of sink and work areas",
                "Provide seating options for food preparation tasks"
              ]
            }
          ]
        },
        implementationProcess: {
          title: "Implementation Process",
          steps: [
            {
              step: "Comprehensive Assessment",
              details: "Conduct a thorough room-by-room assessment using a standardized checklist"
            },
            {
              step: "Prioritization",
              details: "Identify high-impact, low-cost modifications to address first"
            },
            {
              step: "Resource Identification",
              details: "Determine funding sources, equipment needs, and installation requirements"
            },
            {
              step: "Implementation Plan",
              details: "Create a timeline with responsible parties for each modification"
            },
            {
              step: "Education",
              details: "Train staff, residents, and family members on the purpose and proper use of modifications"
            },
            {
              step: "Evaluation",
              details: "Assess effectiveness and make adjustments as needed"
            }
          ]
        },
        facilitySpecificConsiderations: {
          title: "Facility-Specific Considerations",
          settings: [
            {
              setting: "Skilled Nursing Facilities",
              considerations: [
                "Balance resident safety with regulatory requirements",
                "Implement consistent environmental checks during shift changes",
                "Ensure proper lighting levels in all common areas",
                "Maintain clear pathways in dining and activity areas",
                "Provide adequate seating with armrests in common areas"
              ]
            },
            {
              setting: "Assisted Living",
              considerations: [
                "Respect resident autonomy while addressing safety concerns",
                "Implement unobtrusive modifications that maintain homelike environment",
                "Ensure emergency call systems are accessible throughout apartments",
                "Consider smart home technology for lighting and monitoring",
                "Provide education on safe navigation of common areas"
              ]
            },
            {
              setting: "Memory Care Units",
              considerations: [
                "Implement color coding and visual cues for orientation",
                "Ensure consistent lighting to reduce visual misperception",
                "Create safe wandering paths with minimal obstacles",
                "Use contrasting colors for important features (toilet seats, handrails)",
                "Minimize pattern use that may cause visual confusion"
              ]
            }
          ]
        },
        knowledgeCheck: {
          title: "Knowledge Check",
          description: "Test your understanding of environmental modifications",
          questions: [
            {
              question: "Which bathroom modification has the strongest evidence for fall prevention?",
              options: ["Shower chairs", "Grab bars", "Non-slip mats", "Raised toilet seats"],
              answer: "Grab bars",
              explanation: "Properly installed grab bars near toilets and in bathing areas have the strongest evidence for preventing falls in the bathroom."
            },
            {
              question: "What is the first step in implementing home modifications?",
              options: ["Installing equipment", "Comprehensive assessment", "Educating residents", "Removing all rugs"],
              answer: "Comprehensive assessment",
              explanation: "A thorough assessment is essential to identify specific hazards and determine the most appropriate and effective modifications."
            },
            {
              question: "Which flooring type is safest for fall prevention?",
              options: ["Highly polished hardwood", "Low-pile carpet with firm padding", "Thick shag carpet", "Ceramic tile"],
              answer: "Low-pile carpet with firm padding",
              explanation: "Low-pile carpet with firm padding provides secure footing while minimizing trip hazards and offering some cushioning if falls occur."
            }
          ]
        }
      }
    },
    {
      id: "2.4",
      title: "Proper Use of Assistive Devices",
      description: "Learn about various assistive devices for mobility and how to ensure their proper use to prevent falls.",
      duration: 25,
      type: "Video Lesson",
      content: {
        introduction: "Assistive devices can significantly enhance mobility and independence while reducing fall risk when properly selected, fitted, and used. However, improper use can actually increase fall risk, making proper training and ongoing assessment essential.",
        keyStats: [
          "Properly fitted walking aids can reduce fall risk by up to 60%",
          "47% of older adults use assistive devices incorrectly, increasing fall risk",
          "Regular reassessment of assistive devices can reduce falls by 30%",
          "Over 35% of falls involve improper use of or inappropriate assistive devices"
        ],
        commonDevices: {
          title: "Common Assistive Devices",
          devices: [
            {
              name: "Standard Walker",
              description: "Four-legged frame without wheels that must be lifted for each step",
              bestFor: "Individuals needing maximum stability who have adequate upper body strength",
              considerations: "Requires lifting, which can be difficult for some; slower mobility; not suitable for uneven surfaces",
              properUse: [
                "Stand inside the walker, not behind it",
                "Adjust height so elbows are at approximately 30° angle when hands are on grips",
                "Lift walker forward, then step into it",
                "Ensure all four legs contact the ground before bearing weight"
              ]
            },
            {
              name: "Rolling Walker (2-Wheel)",
              description: "Walker with wheels on front legs, back legs have rubber tips",
              bestFor: "Individuals who have difficulty lifting a standard walker but still need significant stability",
              considerations: "Easier to move than standard walker; moderate stability; requires some control to prevent rolling too quickly",
              properUse: [
                "Stand inside the walker with good posture",
                "Roll the walker forward rather than lifting it",
                "Take steps into the walker, not around it",
                "Apply downward pressure for stability when needed"
              ]
            },
            {
              name: "Rollator (4-Wheel)",
              description: "Walker with four wheels, hand brakes, and usually a seat",
              bestFor: "Individuals who need support but have reasonable balance and need to walk longer distances",
              considerations: "Provides less stability than standard walkers; requires hand strength for brakes; allows for rest periods with seat",
              properUse: [
                "Ensure brakes are functioning before each use",
                "Use brakes when standing from seated position",
                "Maintain an upright posture, avoiding leaning forward",
                "Lock brakes before sitting on the seat",
                "Adjust to proper height with wrists level with grips when standing inside"
              ]
            },
            {
              name: "Quad Cane",
              description: "Cane with four-point base for increased stability",
              bestFor: "Individuals needing moderate support on one side",
              considerations: "More stable than standard cane; heavier; wider base requires attention to placement",
              properUse: [
                "Hold on side opposite to weakness/injury",
                "Move cane forward simultaneously with the weaker/affected leg",
                "Ensure all four points contact the ground",
                "Adjust height so elbow is bent approximately 30°"
              ]
            },
            {
              name: "Standard Cane",
              description: "Single-point cane, often with curved handle",
              bestFor: "Individuals needing minimal support or balance assistance",
              considerations: "Least stable option; lightest weight; easiest to maneuver in tight spaces",
              properUse: [
                "Hold on side opposite to weakness/injury",
                "Move cane forward with opposite leg",
                "Adjust height so elbow is bent approximately 30°",
                "Ensure rubber tip is intact and not worn"
              ]
            },
            {
              name: "Crutches",
              description: "Underarm or forearm supports for significant weight-bearing restrictions",
              bestFor: "Individuals who cannot bear full weight on one or both legs",
              considerations: "Require significant upper body strength and coordination; different gait patterns based on weight-bearing status",
              properUse: [
                "Ensure proper height (2-3 finger widths below armpit for underarm crutches)",
                "Bear weight on hands, not underarms, to prevent nerve damage",
                "Follow prescribed gait pattern (3-point, 4-point, swing-to, swing-through)",
                "Maintain proper posture with shoulders relaxed"
              ]
            }
          ]
        },
        selectionProcess: {
          title: "Selection and Fitting Process",
          steps: [
            {
              step: "Assessment of Needs",
              details: "Evaluate mobility limitations, balance, strength, cognitive status, and home environment"
            },
            {
              step: "Device Selection",
              details: "Choose appropriate device based on stability needs, functional abilities, and environmental considerations"
            },
            {
              step: "Proper Measurement",
              details: "Ensure correct height and size for user's body dimensions"
            },
            {
              step: "Initial Training",
              details: "Provide comprehensive instruction on proper use, including specific gait patterns"
            },
            {
              step: "Environmental Adaptation",
              details: "Assess and modify home environment to accommodate device use"
            },
            {
              step: "Regular Reassessment",
              details: "Schedule periodic evaluations to ensure continued appropriateness and proper use"
            }
          ]
        },
        commonErrors: {
          title: "Common Errors and Corrections",
          errors: [
            {
              error: "Improper Height Adjustment",
              consequences: "Excessive bending, shoulder elevation, poor posture, increased fall risk",
              correction: "Adjust device so elbow is bent approximately 30° when standing with good posture"
            },
            {
              error: "Poor Posture/Leaning",
              consequences: "Back pain, reduced stability, altered gait mechanics",
              correction: "Practice maintaining upright posture; consider physical therapy for core strengthening"
            },
            {
              error: "Incorrect Gait Pattern",
              consequences: "Reduced stability, inefficient movement, increased energy expenditure",
              correction: "Review and practice proper sequencing with healthcare provider"
            },
            {
              error: "Improper Placement",
              consequences: "Device too far ahead or behind, causing reaching or instability",
              correction: "Place device at appropriate distance to maintain center of gravity within base of support"
            },
            {
              error: "Using Worn Equipment",
              consequences: "Reduced stability, equipment failure, falls",
              correction: "Regularly inspect tips, wheels, brakes; replace worn components"
            }
          ]
        },
        trainingProtocol: {
          title: "Comprehensive Training Protocol",
          components: [
            {
              component: "Initial Demonstration",
              details: "Healthcare provider demonstrates proper use with clear explanation"
            },
            {
              component: "Supervised Practice",
              details: "User practices with close supervision in controlled environment"
            },
            {
              component: "Environmental Challenges",
              details: "Gradually introduce real-world challenges (different surfaces, doorways, etc.)"
            },
            {
              component: "Return Demonstration",
              details: "User demonstrates proper use independently"
            },
            {
              component: "Family/Caregiver Training",
              details: "Include support persons in training to ensure consistent assistance"
            },
            {
              component: "Written Instructions",
              details: "Provide clear written/visual materials for reference"
            },
            {
              component: "Follow-up Assessment",
              details: "Schedule check-in to evaluate continued proper use and address questions"
            }
          ]
        },
        knowledgeCheck: {
          title: "Knowledge Check",
          description: "Test your understanding of assistive devices",
          questions: [
            {
              question: "Which assistive device provides the most stability?",
              options: ["Standard cane", "Quad cane", "Rollator (4-wheel walker)", "Standard walker"],
              answer: "Standard walker",
              explanation: "A standard walker provides the most stability as it has four points of contact with the ground and must be deliberately moved forward, unlike wheeled devices."
            },
            {
              question: "What is the correct height for a properly fitted walker?",
              options: ["At hip level", "At wrist level when arms hang at sides", "At chest level", "At knee level"],
              answer: "At wrist level when arms hang at sides",
              explanation: "The top of the walker should align with the user's wrist when standing with arms relaxed at sides, allowing for a 30° elbow bend when gripping the handles."
            },
            {
              question: "Which is a sign that an assistive device needs adjustment?",
              options: ["User maintains upright posture", "Elbows bent at 30°", "User leans forward while walking", "Device moves with proper gait pattern"],
              answer: "User leans forward while walking",
              explanation: "Leaning forward while using an assistive device indicates improper height adjustment or that a different device may be needed to provide adequate support."
            }
          ]
        }
      }
    },
    {
      id: "2.5",
      title: "Standardized Assessment Tests",
      description: "Learn about evidence-based assessment tools to evaluate fall risk and how to properly administer them.",
      duration: 20,
      type: "Interactive Lesson",
      content: {
        introduction: "Standardized assessment tools provide objective measures of fall risk and help identify specific areas for intervention. Proper administration and interpretation of these tools are essential for developing effective fall prevention strategies.",
        keyStats: [
          "Standardized assessments can predict fall risk with up to 85% accuracy",
          "Regular reassessment can reduce falls by up to 24%",
          "Combining multiple assessment tools increases predictive value by 30%",
          "Targeted interventions based on assessment findings reduce falls by up to 40%"
        ],
        commonAssessments: {
          title: "Evidence-Based Assessment Tools",
          assessments: [
            {
              name: "Timed Up and Go (TUG) Test",
              purpose: "Assesses mobility, balance, and fall risk",
              procedure: "Time how long it takes a person to rise from a chair, walk 3 meters, turn around, walk back, and sit down",
              scoring: "≥12 seconds indicates increased fall risk in community-dwelling older adults; ≥20 seconds indicates significant mobility problems",
              strengths: "Quick, requires minimal equipment, highly predictive, assesses multiple components",
              limitations: "May not identify specific deficits, less sensitive for very high-functioning individuals"
            },
            {
              name: "Berg Balance Scale (BBS)",
              purpose: "Comprehensive assessment of static and dynamic balance abilities",
              procedure: "14-item scale with tasks of increasing difficulty, from sitting to standing on one foot",
              scoring: "Each item scored 0-4; total score 0-56; ≤45 indicates increased fall risk; ≤40 indicates high fall risk",
              strengths: "Comprehensive, well-validated, assesses multiple aspects of balance",
              limitations: "Takes 15-20 minutes to administer, requires training, ceiling effect for higher functioning individuals"
            },
            {
              name: "Functional Reach Test",
              purpose: "Assesses forward stability limits and fall risk",
              procedure: "Measure the maximum distance a person can reach forward while standing in a fixed position",
              scoring: "<6 inches indicates limited functional balance and increased fall risk; <4 inches indicates significant risk",
              strengths: "Quick, requires minimal equipment, good predictor of recurrent falls",
              limitations: "Assesses only one aspect of balance, may be difficult for those with shoulder problems"
            },
            {
              name: "Tinetti Performance Oriented Mobility Assessment (POMA)",
              purpose: "Evaluates gait and balance to determine fall risk",
              procedure: "Two-part assessment: balance (9 items) and gait (7 items) with specific maneuvers",
              scoring: "Total score 0-28; ≤18 indicates high fall risk; 19-23 indicates moderate risk",
              strengths: "Comprehensive, evaluates both static and dynamic balance, identifies specific deficits",
              limitations: "Takes 10-15 minutes to administer, requires training, some subjectivity in scoring"
            },
            {
              name: "Five Times Sit-to-Stand Test (FTSTS)",
              purpose: "Assesses lower extremity strength and transitional movements",
              procedure: "Time how long it takes to stand up and sit down five times as quickly as possible",
              scoring: ">12 seconds indicates increased fall risk in community-dwelling older adults",
              strengths: "Quick, requires only a chair, good measure of functional lower extremity strength",
              limitations: "Limited to lower body strength assessment, may be difficult for those with severe arthritis"
            },
            {
              name: "Morse Fall Scale",
              purpose: "Assesses fall risk in acute care and long-term care settings",
              procedure: "Six-item assessment including history of falling, secondary diagnoses, ambulatory aids, IV/heparin lock, gait, and mental status",
              scoring: "Total score 0-125; 0-24: low risk; 25-44: moderate risk; ≥45: high risk",
              strengths: "Quick, specifically designed for institutional settings, includes medical factors",
              limitations: "Less applicable to community settings, some subjectivity in scoring"
            }
          ]
        },
        administrationGuidelines: {
          title: "Proper Administration Guidelines",
          guidelines: [
            {
              guideline: "Standardized Environment",
              details: "Conduct assessments in a consistent, well-lit environment with minimal distractions"
            },
            {
              guideline: "Proper Instructions",
              details: "Provide clear, concise instructions using standardized language from assessment protocols"
            },
            {
              guideline: "Demonstration",
              details: "Demonstrate tasks when appropriate before asking the individual to perform them"
            },
            {
              guideline: "Safety Precautions",
              details: "Ensure proper guarding techniques and have assistance available if needed"
            },
            {
              guideline: "Consistent Equipment",
              details: "Use the same equipment (chairs, measuring devices) for all assessments"
            },
            {
              guideline: "Documentation",
              details: "Record results immediately using standardized forms"
            },
            {
              guideline: "Regular Reassessment",
              details: "Schedule periodic reassessments to track changes over time"
            }
          ]
        },
        interpretationAndApplication: {
          title: "Interpretation and Application of Results",
          steps: [
            {
              step: "Score Calculation",
              details: "Calculate scores according to standardized protocols"
            },
            {
              step: "Risk Stratification",
              details: "Categorize fall risk level based on established cutoff scores"
            },
            {
              step: "Deficit Identification",
              details: "Analyze specific components to identify areas of weakness"
            },
            {
              step: "Intervention Matching",
              details: "Select targeted interventions based on identified deficits"
            },
            {
              step: "Goal Setting",
              details: "Establish measurable goals for improvement on specific parameters"
            },
            {
              step: "Communication",
              details: "Share results with the interdisciplinary team and the individual in understandable terms"
            },
            {
              step: "Progress Monitoring",
              details: "Use reassessment results to evaluate intervention effectiveness"
            }
          ]
        },
        selectionGuidelines: {
          title: "Assessment Selection Guidelines",
          settings: [
            {
              setting: "Acute Care",
              recommended: ["Morse Fall Scale", "STRATIFY", "Hendrich II Fall Risk Model"],
              considerations: "Focus on medical factors, medication effects, and acute changes in condition"
            },
            {
              setting: "Long-Term Care",
              recommended: ["Tinetti POMA", "Berg Balance Scale", "TUG Test"],
              considerations: "Balance comprehensive assessment with practicality for regular reassessment"
            },
            {
              setting: "Home Care",
              recommended: ["TUG Test", "Functional Reach", "Five Times Sit-to-Stand"],
              considerations: "Select tools that can be administered in varied home environments with minimal equipment"
            },
            {
              setting: "Outpatient Rehabilitation",
              recommended: ["Berg Balance Scale", "Tinetti POMA", "Dynamic Gait Index"],
              considerations: "Use comprehensive tools that can track progress over time and guide specific interventions"
            }
          ]
        },
        knowledgeCheck: {
          title: "Knowledge Check",
          description: "Test your understanding of standardized assessments",
          questions: [
            {
              question: "Which assessment tool is most appropriate for quick screening in a busy clinic setting?",
              options: ["Berg Balance Scale", "Tinetti POMA", "Timed Up and Go Test", "Functional Gait Assessment"],
              answer: "Timed Up and Go Test",
              explanation: "The TUG test is quick (typically under 3 minutes to administer), requires minimal equipment, and provides valuable information about mobility and fall risk."
            },
            {
              question: "What is considered a high-risk score on the Berg Balance Scale?",
              options: ["Below 56", "Below 45", "Below 40", "Below 30"],
              answer: "Below 40",
              explanation: "A score of 40 or below on the Berg Balance Scale indicates high fall risk, while scores between 41-45 indicate moderate risk."
            },
            {
              question: "Which factor is most important when administering standardized assessments?",
              options: ["Speed of administration", "Consistency in procedures", "Using multiple assessments simultaneously", "Modifying instructions for each individual"],
              answer: "Consistency in procedures",
              explanation: "Standardized assessments must be administered consistently according to established protocols to ensure valid results that can be compared to normative data and across time."
            }
          ]
        }
      }
    },
    // ... other module 2 lessons
  ];

  const module3_lessons = [
    { isHeader: true, title: 'Caregiver Best Practices and Mobility Assistance' }, // Header for Card 3
    // Lessons from caregiverBestPracticesSubmodules
    {
      id: 'cbp-1.1', 
      title: 'Safe Transfer Techniques', 
      duration: 15, 
      type: 'Video Lesson', 
      description: "Learn proper techniques for safely transferring patients between surfaces.",
      content: `Safe Transfer Techniques for Caregivers...` // Keep string for now
    },
    {
      id: 'cbp-1.2', 
      title: 'Supporting Independence while ensuring safety', 
      duration: 15, 
      type: 'Video Lesson', 
      description: "Learn proper techniques for safely transferring patients between surfaces.",
  
    },
    {
      id: 'cbp-1.3', 
      title: 'Proper Body Mechanics for Caregivers', 
      duration: 15, 
      type: 'Video Lesson', 
      description: "Learn proper techniques for safely transferring patients between surfaces.",
      content: `Safe Transfer Techniques for Caregivers...` // Keep string for now
    },
    {
      id: 'cbp-1.4', 
      title: 'Effective communication to reduce fear of falling', 
      duration: 15, 
      type: 'Video Lesson', 
      description: "Learn proper techniques for safely transferring patients between surfaces.",
      content: `Safe Transfer Techniques for Caregivers...` // Keep string for now
    }
    // ... other module 3 lessons
  ];

  const module4_lessons = [
    { isHeader: true, title: 'Emergency Response and Post-Fall Care' }, // Header for Card 4
    // Lessons from emergencyResponseSubmodules
    {
      id: 'er-1.1', 
      title: 'Immediate Response Protocol', 
      duration: 15, 
      type: 'Simulation', 
      description: "Learn proper emergency response procedures for falls.",
      content: `Emergency Response Procedures for Falls...` // Keep string for now
    },
    // ... other emergency response lessons
    { isHeader: true, title: 'Technology and Innovations in Fall Prevention' }, // Header for Card 5
    // Lessons from monitoringAndTechnologySubmodules
    {
      id: "monitoring-1",
      title: "Monitoring Protocols",
      description: "Implement effective monitoring systems for fall prevention.",
      duration: 15,
      type: "Video Lesson",
      content: `Comprehensive Monitoring Protocols for Fall Prevention...` // Keep string for now
    },
    // ... other technology lessons
  ];

  // Remove old submodule definitions if they are no longer needed directly
  // const introductionSubmodules = [...]; 
  // const assessmentSubmodules = [...];
  // const preventionStrategySubmodules = [...];
  // const caregiverBestPracticesSubmodules = [...];
  // const emergencyResponseSubmodules = [...];
  // const monitoringAndTechnologySubmodules = [...];
  // const interventionSubmodules = [...]; 
  // const monitoringSubmodules = [...];

  // Function to calculate total duration based on lessons array (excluding headers)
  const calculateTotalDuration = (lessons) => {
    return lessons.filter(lesson => !lesson.isHeader).reduce((total, lesson) => total + (lesson.duration || 0), 0);
  };

  return (
    <Container maxWidth="lg" sx={{ pb: 8 }}>
      {/* Course Header */}
      <Box sx={{ mb: 6, pt: 5, pb: 3 }}>
        <Box sx={{ mb: 4.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 2.5, textAlign: 'center' }}>
            Fall Prevention Protocol
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 720, textAlign: 'center', mx: 'auto', mb: 3, fontSize: '1.1rem' }}>
            Essential training for patient safety and care, designed to help caregivers prevent&nbsp;falls.
          </Typography>
        </Box>

        <Tabs 
          value={activeSection} 
          onChange={(e, newValue) => setActiveSection(newValue)}
          centered
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              minHeight: 52,
              py: 1.2,
              px: 2,
            },
            '& .MuiTabs-indicator': {
              height: 3,
              backgroundColor: theme.palette.primary.main
            },
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Tab 
            icon={<SchoolIcon />} 
            iconPosition="start" 
            label="Learning Modules" 
            value="learning"
            sx={{ mr: 3 }}
          />
          <Tab 
            icon={<AssignmentIcon />} 
            iconPosition="start" 
            label="Case-Study Evaluation" 
            value="evaluation"
            sx={{ mr: 3 }}
          />
          <Tab 
            icon={<VideocamIcon />} 
            iconPosition="start" 
            label="Video Evaluation" 
            value="video-evaluation" // Corrected value
          />
        </Tabs>
      </Box>

      {/* Learning Modules Section - Uses Exported Data */}
      <TabPanel value={activeSection} index="learning">
        <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3, fontWeight: 'bold' }}>
          Course Modules
                </Typography>
        {/* Module 1: Introduction */}
        <CourseModule
          module={{
            id: "introduction",
            title: "Introduction & Understanding Risks", 
            description: "Fundamentals of fall prevention and risk identification.", 
            icon: <AutoStoriesIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            duration: calculateTotalDuration(module1_lessons), // Use exported data
            lessons: module1_lessons // Use exported data
          }}
          expanded={expandedModule === 0}
          onChange={() => handleModuleChange(0)}
          completedLessons={completedLessons}
          currentLesson={currentLesson}
          onStartLesson={handleStartLesson}
          courseName="fallPrevention"
        />
        {/* Module 2: Assessment */}
        <Box sx={{ mt: 4 }}>
          <CourseModule
            module={{
              id: "assessment",
              title: "Assessment & Prevention Strategies", 
              description: "Techniques for assessment and implementing prevention strategies.", 
              icon: <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              duration: calculateTotalDuration(module2_lessons), // Use exported data
              lessons: module2_lessons // Use exported data
            }}
             expanded={expandedModule === 1}
             onChange={() => handleModuleChange(1)}
             completedLessons={completedLessons}
             currentLesson={currentLesson}
             onStartLesson={handleStartLesson}
             courseName="fallPrevention"
          />
              </Box>
        {/* Module 3: Intervention */}
         <Box sx={{ mt: 4 }}>
            <CourseModule
              module={{
                id: "intervention",
                title: "Intervention & Caregiver Practices", 
                description: "Clinical interventions and best practices for caregivers.", 
                icon: <ConstructionIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
                duration: calculateTotalDuration(module3_lessons), // Use exported data
                lessons: module3_lessons // Use exported data
              }}
              expanded={expandedModule === 2}
              onChange={() => handleModuleChange(2)}
              completedLessons={completedLessons}
              currentLesson={currentLesson}
              onStartLesson={handleStartLesson}
          courseName="fallPrevention"
            />
                  </Box>
        {/* Module 4: Emergency Response */}
        <Box sx={{ mt: 4 }}>
            <CourseModule
              module={{
                id: "emergency",
                title: "Emergency Response & Post-Fall Care", 
                description: "Immediate response protocols and post-fall assessment procedures.", 
                icon: <LocalHospitalIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
                duration: calculateTotalDuration(module4_lessons.filter(lesson => !lesson.title.includes('Technology'))), // Filter out technology lessons
                lessons: module4_lessons.filter(lesson => !lesson.isHeader || !lesson.title.includes('Technology')) // Filter out technology header and lessons
              }}
              expanded={expandedModule === 3}
              onChange={() => handleModuleChange(3)}
              completedLessons={completedLessons}
              currentLesson={currentLesson}
              onStartLesson={handleStartLesson}
          courseName="fallPrevention"
            />
                  </Box>
        {/* Module 5: Technology and Innovations */}
        <Box sx={{ mt: 4 }}>
            <CourseModule
              module={{
                id: "technology",
                title: "Technology & Innovations in Fall Prevention", 
                description: "Modern technologies and innovative approaches for fall prevention and monitoring.", 
                icon: <DevicesIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
                duration: calculateTotalDuration(module4_lessons.filter(lesson => lesson.isHeader && lesson.title.includes('Technology') || !lesson.isHeader && lesson.id.startsWith('monitoring'))), // Only technology lessons
                lessons: module4_lessons.filter(lesson => lesson.isHeader && lesson.title.includes('Technology') || !lesson.isHeader && lesson.id.startsWith('monitoring')) // Only technology header and lessons
              }}
              expanded={expandedModule === 4}
              onChange={() => handleModuleChange(4)}
              completedLessons={completedLessons}
              currentLesson={currentLesson}
              onStartLesson={handleStartLesson}
          courseName="fallPrevention"
            />
                  </Box>
      </TabPanel>

      {/* Case Study Section */}
      <TabPanel value={activeSection} index="evaluation">
        <Box sx={{ position: 'relative', minHeight: '80vh' }}>
          {/* Case Study Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Title and Status */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: 'text.primary', flex: 1 }}>
                  Fall Risk Assessment Case: {activeCaseStudy === 'case1' ? 'Mrs. Eleanor Vance' : 'Mr. Robert Miller'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    icon={<AccessibilityIcon />} 
                    label={`Age: ${activeCaseStudy === 'case1' ? '82' : '75'}`}
                    color="primary"
                    variant="outlined" 
                    size="small"
                  />
                  <Chip 
                    icon={<MonitorHeartIcon />} 
                    label="Active Case"
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Patient Details and Case Objectives Grid */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Patient Details Card */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Patient Details
          </Typography>
          
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current Position
                    </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    {activeCaseStudy === 'case1' 
                      ? 'Sitting on the edge of her bed attempting to get up' 
                      : 'Standing near bathroom doorway, appears unsteady'}
                    </Typography>
                  </Box>
                  
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Medical Conditions
                  </Typography>
                  <Box sx={{ 
                    backgroundColor: 'background.default',
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    {(activeCaseStudy === 'case1' ? caseStudy.patientInfo.conditions : additionalCaseStudy.patientInfo.conditions).map((condition, index) => (
                      <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ 
                          mb: index !== ((activeCaseStudy === 'case1' ? caseStudy.patientInfo.conditions : additionalCaseStudy.patientInfo.conditions).length - 1) ? 1 : 0,
                          color: 'text.primary',
                          display: 'flex',
                          alignItems: 'center',
                          '&:before': {
                            content: '""',
                            width: '6px',
                            height: '6px',
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                            mr: 1,
                            display: 'inline-block'
                          }
                        }}
                      >
                        {condition}
                      </Typography>
                    ))}
                    </Box>
                  </Box>
              </Paper>
            </Grid>
            
            {/* Case Objectives Card */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  Case Objectives
                </Typography>
                <List sx={{ pt: 0 }}>
                  {(activeCaseStudy === 'case1' ? caseStudy.objectives : additionalCaseStudy.objectives).map((objective, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ArrowRight color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={objective} 
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.primary'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* Progress Card */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Progress
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Completed Steps: {completedSteps.length} / 5
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(completedSteps.length / 5) * 100} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Paper>

          {/* Protocol Steps and Assessment Questions Grid */}
          <Grid container spacing={3}>
            {/* Protocol Steps Card - RESTORED */}
            <Grid item xs={12} md={3}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Protocol Steps
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {(activeCaseStudy === 'case1' ? originalProtocolSteps : additionalProtocolSteps).map((step, index) => (
                    <Box
                      key={index}
                      onClick={() => handleStepClick(index)}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        backgroundColor: completedSteps.includes(index) ? alpha(theme.palette.success.main, 0.1) : 'background.paper',
                        border: 1,
                        borderColor: completedSteps.includes(index) ? 'success.main' : 'divider',
                        '&:hover': {
                          backgroundColor: completedSteps.includes(index) 
                            ? alpha(theme.palette.success.main, 0.15)
                            : alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box 
                          sx={{ 
                            minWidth: 28,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: completedSteps.includes(index) ? 'success.main' : 'primary.main',
                            color: 'white',
                            fontWeight: 600,
                            flexShrink: 0
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 500,
                            flex: 1,
                            mr: 1
                          }}
                        >
                          {step.title}
                        </Typography>
                        {completedSteps.includes(index) && (
                          <CheckCircleIcon color="success" sx={{ flexShrink: 0 }} />
                        )}
                      </Box>
                      <Collapse in={expandedStep === index}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, pl: 5 }}>
                          {step.description}
                        </Typography>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Assessment Questions Card */}
            <Grid item xs={12} md={9}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, position: 'relative', height: '100%' }}>
                {/* Remaining Attempts Display */}
                <Box sx={{ 
                  position: 'absolute',
                  top: 24,
                  right: 24,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Remaining Attempts:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {[...Array(MAX_ATTEMPTS - (activeCaseStudy === 'case1' ? incorrectAttempts : additionalCaseIncorrectAttempts))].map((_, index) => (
                      <Box
                        key={index}
                        sx={{ 
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: 'success.main',
                          opacity: 0.8
                        }}
                      />
                    ))}
                    {[...Array(activeCaseStudy === 'case1' ? incorrectAttempts : additionalCaseIncorrectAttempts)].map((_, index) => (
                      <Box
                        key={`error-${index}`}
                        sx={{ 
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: 'error.main',
                          opacity: 0.8
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3, pr: 20 }}>
                  Assessment Questions
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {(activeCaseStudy === 'case1' ? flashcards : additionalFlashcards).map((card, index) => {
                    const isAnswered = correctlyAnsweredQuestions.has(`${activeCaseStudy}-${index}`);
                    return (
                      <Box key={index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {card.question}
                        </Typography>
                        <Grid container spacing={2}>
                          {card.options.map((option, optionIndex) => (
                            <Grid item xs={12} sm={6} key={optionIndex}>
                              <Button
                                fullWidth
                                variant={isAnswered ? 'outlined' : 'contained'}
                                color={isAnswered ? 
                                  (optionIndex === card.correctAnswer ? 'success' : 'error') 
                                  : 'primary'
                                }
                                onClick={() => {
                                  if (!isAnswered) {
                                    if (optionIndex === card.correctAnswer) {
                                      handleCorrectAnswer(index);
                                    } else {
                                      handleIncorrectAnswer();
                                    }
                                  }
                                }}
                                disabled={isAnswered}
                                sx={{
                                  textTransform: 'none',
                                  opacity: isAnswered ? 
                                    (optionIndex === card.correctAnswer ? 1 : 0.5) 
                                    : 1
                                }}
                              >
                                {option}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                        {isAnswered && (
                          <Alert 
                            severity={correctlyAnsweredQuestions.has(`${activeCaseStudy}-${index}`) ? "success" : "error"}
                            sx={{ mt: 2 }}
                          >
                            {card.explanation}
                          </Alert>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Notifications */}
          <Snackbar
            open={showSuccessNotification}
            autoHideDuration={5000}
            onClose={() => setShowSuccessNotification(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="success" sx={{ width: '100%' }}>
              {firstCaseCompleted && showAdditionalCaseStudy 
                ? "Great job! Moving on to the next case study."
                : "Congratulations! You've completed the case study successfully."}
            </Alert>
          </Snackbar>

          <Snackbar
            open={showErrorNotification}
            autoHideDuration={5000}
            onClose={() => setShowErrorNotification(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="warning" sx={{ width: '100%' }}>
              You've reached the maximum number of incorrect attempts. An additional case study will be provided for practice.
            </Alert>
          </Snackbar>
        </Box>
      </TabPanel>

      {/* Correct Video Evaluation Section */}
      <TabPanel value={activeSection} index="video-evaluation">
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Video-Based Skill Assessment
                </Typography>
          <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
            Upload or record a short video (max 15MB) demonstrating the fall response protocol based on the randomized scenario below. Your demonstration will be analyzed using AI.
                </Typography>

          <Card variant="outlined" sx={{ mb: 4, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderColor: alpha(theme.palette.primary.main, 0.3) }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>Scenario:</Typography>
            <Typography variant="body1">{scenarioText}</Typography>
          </Card>

          {/* Video Upload/Record Grid */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* File Upload Option */}
            <Grid item xs={12} md={6}>
                  <Box
                    component="label"
                htmlFor="video-upload-fp"
                    sx={{
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      p: 3,
                      height: '100%',
                  minHeight: 150,
                      textAlign: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                  transition: 'background-color 0.2s',
                      '&:hover': {
                    bgcolor: 'action.hover',
                  },
                    }}
                  >
                    <input
                      type="file"
                  id="video-upload-fp"
                      accept="video/*"
                      style={{ display: 'none' }}
                  onChange={handleVideoFileChange}
                    />
                    <UploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography sx={{ fontWeight: 500 }}>Upload Video</Typography>
                <Typography variant="body2" color="text.secondary">
                  MP4, MOV, WebM (max 15MB)
                    </Typography>
                  </Box>
                </Grid>

                {/* Camera Recording Option */}
            <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      p: 3,
                      height: '100%',
                  minHeight: 150,
                      textAlign: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                  transition: 'background-color 0.2s',
                      '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={handleCameraControlClick}
                  >
                    {isRecording ? (
                      <>
                        <StopIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                    <Typography color="error.main" sx={{ fontWeight: 500 }}>Stop Recording</Typography>
                      </>
                    ) : (
                      <>
                        <VideocamIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography sx={{ fontWeight: 500 }}>
                      {stream ? 'Start Recording' : 'Use Camera'}
                    </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>

          {/* Live Camera Preview or Video Preview */}
          {(stream || recordedVideo || (uploadedVideo && !recordedVideo)) && (
            <Box sx={{ mt: 3, mb: 3, position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
               <Typography variant="caption" sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 1, py: 0.5, borderRadius: 1, zIndex: 1 }}>
                {stream && !isRecording && 'Live Preview'}
                {isRecording && 'Recording...'}
                {recordedVideo && 'Recorded Preview'}
                {uploadedVideo && !recordedVideo && 'Upload Preview'}
                  </Typography>
                    <video
                ref={videoRef}
                controls={!stream || !!recordedVideo} // Show controls for uploaded/recorded, not live preview
                autoPlay={!!stream && !isRecording} // Autoplay live preview
                muted={!!stream} // Mute live preview
                      width="100%"
                      height="auto"
                src={recordedVideo || (uploadedVideo && !stream ? URL.createObjectURL(uploadedVideo) : '')}
                style={{ display: 'block', borderRadius: '8px' }} // Ensure video fills container
                    />
                  </Box>
          )}

          {/* Analyze Button */}
          {uploadedVideo && !analysisComplete && (
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={analyzing}
              startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ mt: 2, py: 1.5, fontSize: '1rem', fontWeight: 500, borderRadius: 2 }}
                    onClick={async () => {
                        setAnalyzing(true);
                        setAnalysisError(null);
                 setAnalysisResults(null); // Clear previous results
                 setIsAiGenerated(true); // Assume AI analysis unless mock is used
                 try {
                   console.log(`Analyzing video for scenario: ${scenarioText}`);
                   const results = await analyzeVideoWithGemini(uploadedVideo, null, scenarioText);
                   console.log("Analysis results received:", results);
                          setAnalysisResults(results);
                          setAnalysisComplete(true);
                      } catch (error) {
                   console.error("Error analyzing video:", error);
                   setAnalysisError(`Analysis failed: ${error.message}. Please check console or try again.`);
                      } finally {
                        setAnalyzing(false);
                      }
                    }}
                  >
              {analyzing ? 'Analyzing Video...' : 'Analyze My Demonstration'}
                  </Button>
          )}

          {/* Analysis Results */}
                {analysisComplete && analysisResults && (
            <Paper elevation={2} sx={{ mt: 4, p: { xs: 2, sm: 3, md: 4 }, bgcolor: 'background.paper' }}>
                    {analysisResults.isNotFallResponseVideo ? (
                // UI for "Not a Fall Response Video" error - Replicating screenshot structure
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                    AI Analysis Results
                        </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.secondary' }}>
                          Performance Evaluation
                        </Typography>
                        
                  <Alert severity="error" variant="outlined" icon={<WarningIcon />} sx={{ mb: 3, borderColor: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                     <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'error.dark' }}>
                       Not a Fall Response Video
                     </Typography>
                     <Typography variant="body2" sx={{ mb: 2, color: 'error.dark' }}>
                       {analysisResults.feedback} {/* Display the API feedback here */}
                     </Typography>
                     <Paper elevation={0} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
                       <Box sx={{ 
                              width: 60,
                              height: 60,
                         borderRadius: '50%', 
                         bgcolor: 'error.main', 
                         color: 'white',
                              display: 'flex',
                         flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                         flexShrink: 0
                       }}>
                         <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>0</Typography>
                         <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 1 }}>SCORE</Typography>
                          </Box>
                       <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.darker' }}>
                         Your video has received a score of 0 as it does not demonstrate the required fall response protocol.
                            </Typography>
                     </Paper>
                  </Alert>
                  
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                     <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                       <InfoIcon color="primary"/> Feedback
                        </Typography>
                     <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
                       This video does not appear to demonstrate a fall response scenario. To properly evaluate your fall prevention skills, please submit a video that shows you responding to the specific fall scenario provided:
                                    </Typography>
                     <Box sx={{ 
                       border: '1px dashed', 
                       borderColor: 'divider', 
                       borderRadius: 1, 
                       p: 2, 
                       mb: 3, 
                       bgcolor: 'background.paper' 
                     }}>
                       <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                         "{scenarioText}"
                              </Typography>
                            </Box>
                     <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                       What Your Video Should Include:
                            </Typography>
                     <List dense sx={{ mb: 2 }}>
                       <ListItem sx={{ py: 0.2 }}><ListItemIcon sx={{minWidth: 20}}><Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main' }} /></ListItemIcon><ListItemText primary="Clear demonstration of responding to a fall situation based on the scenario" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                       <ListItem sx={{ py: 0.2 }}><ListItemIcon sx={{minWidth: 20}}><Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main' }} /></ListItemIcon><ListItemText primary="Proper assessment techniques appropriate for the patient's condition" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                       <ListItem sx={{ py: 0.2 }}><ListItemIcon sx={{minWidth: 20}}><Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main' }} /></ListItemIcon><ListItemText primary="Safe movement practices when assisting the patient" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                       <ListItem sx={{ py: 0.2 }}><ListItemIcon sx={{minWidth: 20}}><Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main' }} /></ListItemIcon><ListItemText primary="Condition-specific considerations for any mentioned conditions (e.g., COPD, dementia)" primaryTypographyProps={{ variant: 'body2' }} /></ListItem>
                     </List>
                     <Typography variant="body1" paragraph>
                       Please record a new video showing your response to the fall scenario. This will allow us to properly evaluate your skills and provide meaningful feedback on your fall prevention techniques.
                              </Typography>
              </Paper>

                            </Box>
              ) : (
                // UI for Actual Analysis Results (Score, Table, Feedback - Stacked)
                <Box>
                  {/* ... Headers and Score Display remain the same ... */}
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    AI Analysis Results
                            </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      letterSpacing: '0.5px',
                      mb: 4 
                    }}
                  >
                    Performance Evaluation
                </Typography>

                  {/* Scoring System comments */}
                  {/* ... existing comments ... */}

                  <Paper 
                    elevation={0} 
                    sx={{
                      p: 4,
                      mb: 4,
                      borderRadius: 3,
                      background: (theme) => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    {/* Score Circle */}
                  <Box
                    sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        bgcolor: analysisResults.score >= 75 ? 'success.main' : 
                                analysisResults.score >= 50 ? 'warning.main' : 'error.main',
                        color: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                        boxShadow: (theme) => `0 8px 24px ${alpha(
                          analysisResults.score >= 75 ? theme.palette.success.main : 
                          analysisResults.score >= 50 ? theme.palette.warning.main : 
                          theme.palette.error.main, 0.25
                        )}`,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Typography 
                        variant="h3" 
                    sx={{
                          fontWeight: 700, 
                          lineHeight: 1,
                          mb: 0.5
                        }}
                      >
                        {analysisResults.score}
                </Typography>
                      <Typography 
                        variant="caption" 
                sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.75rem'
                        }}
                      >
                        Score
                    </Typography>
                </Box>

                    {/* Score Details */}
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="h4" 
                          sx={{
                          fontWeight: 700,
                          color: analysisResults.score >= 85 ? 'success.main' : 
                                 analysisResults.score >= 70 ? 'primary.main' :
                                 analysisResults.score >= 50 ? 'warning.main' : 'error.main',
                          mb: 1
                        }}
                      >
                        {analysisResults.score >= 85 ? 'Excellent' : 
                         analysisResults.score >= 70 ? 'Good' : 
                         analysisResults.score >= 50 ? 'Fair' : 'Needs Improvement'}
                          </Typography>
                      <Typography 
                        variant="body1" 
                              sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          maxWidth: '600px'
                        }}
                      >
                        {analysisResults.scoreJustification || 'Good performance with adherence to safety protocols.'}
                              </Typography>
                              </Box>
                  </Paper>

                  {/* Detailed Analysis Table */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
                      Detailed Analysis
                    </Typography>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      mb: 3,
                      '& .MuiTableRow-root:nth-of-type(odd)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      },
                      '& .MuiTableRow-root:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    {/* ... existing Table structure ... */}
                     <Table sx={{ minWidth: 650 }} aria-label="analysis table">
                          <TableHead sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, width: '25%' }}>Safety Aspect</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, width: '15%' }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: '30%' }}>Observations</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: '30%' }}>Recommendations</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analysisResults.results && analysisResults.results
                              .filter(row => 
                                // Filter out rows with N/A or redundant values
                                row.safetyAspect !== 'N/A' && 
                                row.safetyAspect !== 'Safety Aspect' &&
                                row.observations !== 'N/A' &&
                                row.recommendations !== 'N/A'
                              )
                              .map((row, index) => (
                              <TableRow 
                                key={index}
                                sx={{ 
                                  '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                                  '&:hover': { backgroundColor: '#f0f7ff' },
                                  '& td': { py: 2 }
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500 }}>
                                    {`${index + 1}. ${row.safetyAspect?.replace(/\*\*/g, '').replace(/^\d+\.\s*/, '')}`}
                                </TableCell>
                                <TableCell align="center">
                                    {row.status === '✅' || row.status?.toLowerCase() === 'good' || row.status?.toLowerCase() === 'correct' ? (
                                    <Chip 
                                        label="Did Well" 
                                      size="small" 
                                      color="success" 
                                      sx={{ fontWeight: 500, color: '#fff' }}
                                    />
                                    ) : row.status === '⚠️' || row.status?.toLowerCase() === 'warning' || row.status?.toLowerCase() === 'partial' ? (
                                    <Chip 
                                        label="Needs Attention" 
                                      size="small" 
                                      color="warning" 
                                      sx={{ fontWeight: 500, color: '#fff' }}
                                    />
                                  ) : (
                                    <Chip 
                                      label="Alert" 
                                      size="small" 
                                      color="error" 
                                      sx={{ fontWeight: 500, color: '#fff' }}
                                    />
                                  )}
                                </TableCell>
                                  <TableCell sx={{ color: 'text.secondary' }}>{row.observations}</TableCell>
                                  <TableCell sx={{ color: 'text.secondary' }}>{row.recommendations}</TableCell>
                              </TableRow>
                            ))}
                            {(!analysisResults.results || analysisResults.results.length === 0) && (
                              <TableRow><TableCell colSpan={4} align="center">Detailed analysis table not available.</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                  {/* Feedback Section - Now below the table */}
                    <Box sx={{ 
                    mt: 4, 
                    pr: 3,
                    pt: 3,
                    pb: 3,
                    bgcolor: 'transparent'
                  }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      mb: 3,
                      display: 'flex',
                      color: 'text.primary',
                      pb: 2,
                      fontSize: '1.75rem'
                    }}>
                        Feedback
                      </Typography>
                      
                    {/* What You Did Well Section */}
                    <Typography variant="h6" sx={{ 
                      color: 'success.main',  // Matches the "Did Well" badge color
                      fontSize: '1.25rem', 
                      fontWeight: 600, 
                      mb: 3,
                      mt: 4,
                      pb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}>
                          What You Did Well:
                        </Typography>
                    <Box component="ul" sx={{ 
                      listStyle: 'none', 
                      pl: 0, 
                      mb: 4
                    }}>
                      {analysisResults.positivePoints?.map((point, index) => (
                        <Grid container spacing={2} key={index} sx={{ mb: 1.5, alignItems: 'flex-start' }}>
                          <Grid item xs={12} sm={4}>
                            <Typography component="span" sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                            }}>
                              {point.category}
                              {point.aspectNumber && ` (#${point.aspectNumber})`}:
                          </Typography>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Typography component="span" sx={{ color: 'text.secondary' }}>
                              {point.detail}
                          </Typography>
                          </Grid>
                        </Grid>
                      ))}
                        </Box>
                        
                    {/* Important Steps Section */}
                    <Typography variant="h6" sx={{ 
                      color: 'error.main',  // Matches the "Alert" badge color
                      fontSize: '1.25rem', 
                      fontWeight: 600, 
                      mb: 3,
                      mt: 6,  // Increased from 4
                      pb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}>
                          Important Steps to Include:
                        </Typography>
                    <Box component="ul" sx={{ 
                      listStyle: 'none', 
                      pl: 0, 
                      mb: 6  // Increased from 4
                    }}>
                      {analysisResults.importantSteps?.map((step, index) => (
                        <Grid container spacing={2} key={index} sx={{ mb: 1.5, alignItems: 'flex-start' }}>
                          <Grid item xs={12} sm={4}>
                            <Typography component="span" sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                            }}>
                              {step.category}
                              {step.aspectNumber && ` (#${step.aspectNumber})`}:
                          </Typography>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Typography component="span" sx={{ color: 'text.secondary' }}>
                              {step.detail}
                          </Typography>
                          </Grid>
                        </Grid>
                      ))}
                        </Box>
                        
                    {/* Suggestions Section */}
                    <Typography variant="h6" sx={{ 
                      color: 'warning.main',  // Matches the "Needs Attention" badge color
                      fontSize: '1.25rem', 
                      fontWeight: 600, 
                      mb: 3,
                      mt: 6,  // Increased from 4
                      pb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}>
                          Suggestions for Enhancement:
                        </Typography>
                    <Box component="ul" sx={{ 
                      listStyle: 'none', 
                      pl: 0, 
                      mb: 6  // Increased from 4
                    }}>
                      {analysisResults.suggestions?.map((suggestion, index) => (
                        <Grid container spacing={2} key={index} sx={{ mb: 1.5, alignItems: 'flex-start' }}>
                          <Grid item xs={12} sm={4}>
                            <Typography component="span" sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                            }}>
                              {suggestion.category}
                              {suggestion.aspectNumber && ` (#${suggestion.aspectNumber})`}:
                          </Typography>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Typography component="span" sx={{ color: 'text.secondary' }}>
                              {suggestion.detail}
                          </Typography>
                          </Grid>
                        </Grid>
                      ))}
                        </Box>
                        
                    {/* Conclusion */}
                    <Typography variant="body1" sx={{ 
                      mt: 3, 
                      pt: 2, 
                      color: 'text.secondary',
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}>
                      Your performance in handling this fall situation showed both strengths and areas for development. With practice and attention to these additional elements, you'll continue to enhance your care approach. Review the detailed analysis above for specific guidance on improving your fall management skills.
                        </Typography>
                      </Box>
                      
                  </Box>
                )}

              {/* Common Button - Try New Scenario */}
               <Button variant="outlined" onClick={() => {
                  setAnalysisComplete(false);
                  setAnalysisResults(null);
                  setUploadedVideo(null);
                  setRecordedVideo(null);
                  setAnalysisError(null);
                  setStream(null); // Ensure camera stream is stopped/cleared
                  if (videoRef.current) videoRef.current.srcObject = null; // Clear video src
                  setScenarioText(generateRandomScenario()); // Generate a new scenario
               }} sx={{ mt: 3 }}>
                 Try New Scenario
               </Button>
              </Paper>
            )}

          {/* Error Display */}
            {analysisError && (
            <Alert severity="error" sx={{ mt: 3 }}>
                  {analysisError}
            </Alert>
            )}

        </Paper>
      </TabPanel>
    </Container>
  );
}

export default FallPreventionCourse;
